import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { 
  RefreshCw, 
  Award, 
  AlertTriangle, 
  Users, 
  Car, 
  Calendar,
  Building,
  Search,
  Clock,
  X,
  Navigation,
  Truck,
  Timer,
  DollarSign,
  Check,
  Trash2,
  User,
  MapPin,
  Satellite,
  Route,
  Globe,
  Map
} from 'lucide-react';
import { Dialog } from '@headlessui/react';

const GoldLayer = () => {
  const { isAdmin, user } = useAuth();
  
  // Generalized function to check if there were office/vendor stops before first job
    const hasOfficeOrVendorStopBeforeFirstJob = (techData, assignmentsData = null, employeeInfo = null) => {
    const locationArrivals = techData.location_arrivals || techData.gps_tracking_data?.location_arrivals;
    

     
    if (!locationArrivals) return false;
    
    try {

      
      // Get first job time
      let firstJobTime = null;
      if (techData.first_job_gps_arrival || techData.gps_tracking_data?.first_job_gps_arrival) {
        firstJobTime = new Date(techData.first_job_gps_arrival || techData.gps_tracking_data.first_job_gps_arrival);
      } else if (techData.first_job_st_arrival || techData.gps_tracking_data?.first_job_st_arrival) {
        firstJobTime = new Date(techData.first_job_st_arrival || techData.gps_tracking_data.first_job_st_arrival);
      } else {
        const assignments = assignmentsData || techData.assignments || techData.gps_tracking_data?.assignments;
        if (assignments && assignments.length > 0) {
          // Try multiple fields to find first job time
          const firstAssignment = assignments[0];
          if (firstAssignment.st_dispatched_on) {
            firstJobTime = new Date(firstAssignment.st_dispatched_on);
          } else if (firstAssignment.arrived_on) {
            firstJobTime = new Date(firstAssignment.arrived_on);
          } else if (firstAssignment.scheduled_start) {
            firstJobTime = new Date(firstAssignment.scheduled_start);
          }
        }
      }
      
      if (!firstJobTime) return false;
      
      const locationData = typeof locationArrivals === 'string' 
        ? JSON.parse(locationArrivals) 
        : locationArrivals;
      

      
      if (locationData && locationData.all_visits && Array.isArray(locationData.all_visits)) {
        return locationData.all_visits.some(visit => {
          if (visit.arrival_time) {
            const arrivalTime = new Date(visit.arrival_time);
            const isBeforeFirstJob = arrivalTime < firstJobTime;
          
            // Office OR vendor arrivals before first job eliminate drive time for all tenants
            if ((visit.location_type === 'office' || visit.location_type === 'vendor') && isBeforeFirstJob) {
              return true;
            }
          }
          return false;
        });
      }
    } catch (error) {
      console.error('Error checking office/vendor stops before first job:', error);
    }
    return false;
  };

  // Generalized function to calculate drive time with office/vendor stop logic
  const calculateMorningDriveTime = (techData, assignmentsData = null, employeeInfo = null) => {
    const commuteData = techData.commute_data || techData.gps_tracking_data?.commute_data;
    if (!commuteData) return 0;
    
    const morningCommute = commuteData.morning_commute;
    const isDoorToDoorDay = commuteData.is_door_to_door_day;
    
    const azugaTime = morningCommute?.minutes || 0;
    const googleTime = morningCommute?.expected_duration_minutes || 0;
    const baseMorningDriveTime = Math.min(azugaTime, googleTime);
    
    // Check if there were office or vendor stops before first job
    const hasStopBeforeJob = hasOfficeOrVendorStopBeforeFirstJob(techData, assignmentsData, employeeInfo);
    
    // Don't give drive time if they stopped at office or vendor before first job
    const morningDriveTime = hasStopBeforeJob ? 0 : baseMorningDriveTime;
    
    // For door-to-door days (Sundays/holidays), don't subtract 45 minutes
    return isDoorToDoorDay ? morningDriveTime : Math.max(0, morningDriveTime - 45);
  };
  
  const [loading, setLoading] = useState(true);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [technicianDataLoading, setTechnicianDataLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshInProgress, setRefreshInProgress] = useState(false);
  const [refreshDetails, setRefreshDetails] = useState(null);
  const [dashboard, setDashboard] = useState(null);
 
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showDiscrepancyDetail, setShowDiscrepancyDetail] = useState(false);
  
  // Refresh date state
  const [refreshStartDate, setRefreshStartDate] = useState(new Date());
  const [refreshEndDate, setRefreshEndDate] = useState(new Date());
  
  // Unified filter states for all tabs
  const [globalFilters, setGlobalFilters] = useState(() => {
    // Load saved filters from localStorage on component mount
    const savedFilters = localStorage.getItem('goldLayerFilters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        let savedDate = new Date();
        
        // Validate the saved date
        if (parsed.date) {
          const dateObj = new Date(parsed.date);
          if (!isNaN(dateObj.getTime())) {
            savedDate = dateObj;
          }
        }
        
        return {
          date: savedDate,
          tenant: parsed.tenant || '',
          work_location: parsed.work_location || '',
          exclude_apprentices: parsed.exclude_apprentices || false
        };
      } catch (error) {
        console.error('Error parsing saved filters:', error);
      }
    }
    return {
      date: new Date(),
      tenant: '',
      work_location: '',
      exclude_apprentices: false
    };
  });
  const [availableTenants, setAvailableTenants] = useState([]);



  // Reusable modal state for both discrepancies and technician summaries
  const [selectedDetailData, setSelectedDetailData] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailModalType, setDetailModalType] = useState('discrepancy'); // 'discrepancy' or 'technician'

  // Technician summary state
  const [technicianData, setTechnicianData] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [technicianDetailData, setTechnicianDetailData] = useState(null);
  const [showTechnicianDetailModal, setShowTechnicianDetailModal] = useState(false);

  // Add pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalEmployees, setTotalEmployees] = useState(0);

  // Add debounced search term state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 600); // 600ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update employee search when debounced term changes
  useEffect(() => {
    setEmployeeSearch(debouncedSearchTerm);
    setPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    fetchData();
    fetchTenants();
    if (isAdmin()) {
      checkRefreshStatus();
    }
  }, []);

  // Check refresh status periodically (admin-only API)
  useEffect(() => {
    if (!isAdmin()) {
      return;
    }
    const interval = setInterval(checkRefreshStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [user?.is_superuser]);

  // Auto-refresh view summary every 15 seconds when on dashboard tab (where technician hours are shown)
  useEffect(() => {
    if (activeTab !== 'dashboard') {
      return; // Don't run if not on dashboard tab
    }

    console.log('Starting auto-refresh for view summary on dashboard tab');
    const interval = setInterval(() => {
      // Skip refresh if modal is open to prevent scroll position reset
      if (showTechnicianDetailModal) {
        console.log('Skipping auto-refresh: modal is open');
        return;
      }
      console.log('Auto-refreshing view summary...');
      refreshViewSummary();
    }, 15000); // Refresh every 15 seconds

    return () => {
      console.log('Stopping auto-refresh for view summary');
      clearInterval(interval);
    };
  }, [activeTab, showTechnicianDetailModal, globalFilters.date, globalFilters.tenant, globalFilters.work_location, globalFilters.exclude_apprentices]);

  const checkRefreshStatus = async () => {
    try {
      const response = await axios.get('/api/gold/refresh/status');
      setRefreshInProgress(response.data.in_progress);
      setRefreshDetails(response.data.details);
    } catch (error) {
      // Let axios interceptor handle 401 errors automatically; 403 if not admin
      const s = error.response?.status;
      if (s !== 401 && s !== 403) {
        console.error('Error checking refresh status:', error);
      }
    }
  };

  const formatDuration = (startTime) => {
    if (!startTime) return '';
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now - start;
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    
    if (diffMins > 0) {
      return `${diffMins}m ${diffSecs}s`;
    }
    return `${diffSecs}s`;
  };

  const formatOperationType = (type) => {
    switch (type) {
      case 'full_refresh': return 'Full Refresh';
      case 'sequence_refresh': return 'Sequence Refresh';
      case 'employee_master_refresh': return 'Employee Master Refresh';
      default: return type;
    }
  };



  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchTechnicianData();
    }
  }, [activeTab, globalFilters.date, globalFilters.tenant, globalFilters.work_location, globalFilters.exclude_apprentices]);



  const fetchTenants = async () => {
    try {
      // Get unique tenants from the new tenants endpoint
      const response = await axios.get('/api/gold/tenants');
      // Keep the full tenant structure to access work locations
      setAvailableTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  // Helper function to format date for API
  const formatDateForAPI = (date) => {
    if (!date) return '';
    // Get year, month, and day components and pad with zeros if needed
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchTechnicianData = async () => {
    setTechnicianDataLoading(true);
    try {
      const params = new URLSearchParams();
      if (globalFilters.date) {
        const formattedDate = formatDateForAPI(globalFilters.date);
        params.append('date', formattedDate);
      }
      if (globalFilters.tenant) params.append('tenant', globalFilters.tenant);
      if (globalFilters.work_location) params.append('work_location', globalFilters.work_location);
      if (globalFilters.exclude_apprentices) params.append('exclude_apprentices', 'true');
      
      console.log('Fetching technician data with params:', {
        date: globalFilters.date,
        tenant: globalFilters.tenant,
        work_location: globalFilters.work_location,
        exclude_apprentices: globalFilters.exclude_apprentices,
        url: `/gold/technician-hours?${params}`
      });
      
      // Call both APIs in parallel
      const [technicianResponse, viewsResponse] = await Promise.all([
        axios.get(`/api/gold/technician-hours?${params}`),
        axios.get(`/api/gold/technician-view-summary?${params}`).catch(error => {
          console.error('Error fetching view summary (non-fatal):', error);
          return { data: { views: [] } };  // Return empty views on error
        })
      ]);
      
      const technicians = technicianResponse.data.technicians || [];
      const views = viewsResponse.data.views || [];
      
      // Build a map of employee_id -> array of views
      const viewsByEmployee = views.reduce((acc, view) => {
        if (!acc[view.employee_id]) {
          acc[view.employee_id] = [];
        }
        acc[view.employee_id].push(view);
        return acc;
      }, {});
      
      // Merge view data into technician data
      const enrichedTechnicians = technicians.map(tech => ({
        ...tech,
        recent_views: viewsByEmployee[tech.employee_id] || []
      }));
      
      setTechnicianData(enrichedTechnicians);
    } catch (error) {
      console.error('Error fetching technician data:', error);
      setTechnicianData([]);
    } finally {
      setTechnicianDataLoading(false);
    }
  };

  // Update fetchData to handle pagination and search
  const fetchData = async () => {
    if (activeTab === 'dashboard') {
      setDashboardLoading(true);
    }
    try {
      const [dashboardResponse, employeesResponse] = await Promise.all([
        activeTab === 'dashboard' ? axios.get('/api/gold/dashboard') : Promise.resolve({ data: dashboard }),
        axios.get('/api/gold/employees', {
          params: {
            limit: pageSize,
            offset: (page - 1) * pageSize,
            search: employeeSearch
          }
        })
      ]);
      
      if (activeTab === 'dashboard') {
        setDashboard(dashboardResponse.data);
      }
      setEmployees(employeesResponse.data.employees || []);
      setTotalEmployees(employeesResponse.data.summary?.total_count || 0);
      
      console.log('Employee data loaded:', employeesResponse.data.employees?.length || 0, 'employees');
    } catch (error) {
      console.error('Error fetching gold layer data:', error);
      setEmployees([]);
      setTotalEmployees(0);
    } finally {
      setLoading(false);
      setDashboardLoading(false);
      setEmployeesLoading(false);
    }
  };

  // Add effect to refetch when page or search changes
  useEffect(() => {
    if (activeTab === 'employees') {
      setEmployeesLoading(true);
      fetchData();
    }
  }, [page, employeeSearch, activeTab]);

  const refreshGoldLayer = async (startDate = null, endDate = null) => {
    setRefreshing(true);
    try {
      setDashboardLoading(true);
      setTechnicianDataLoading(true);
      
      // Use provided dates or default to today
      const start = startDate || refreshStartDate;
      const end = endDate || refreshEndDate;
      
      // Format dates for API - use refresh-sequence instead of refresh-all
      const params = new URLSearchParams();
      params.append('components', 'technician-assignments');
      params.append('components', 'daily-time-summaries');
      params.append('components', 'discrepancies');
      params.append('start_date', formatDateForAPI(start));
      if (end && end !== start) {
        params.append('end_date', formatDateForAPI(end));
      }
      
      await axios.post(`/api/gold/refresh-sequence?${params}`);
      setRefreshInProgress(true);
      fetchData();
      if (activeTab === 'dashboard') fetchTechnicianData();
    } catch (error) {
      console.error('Error refreshing gold layer:', error);
      if (error.response?.status === 409) {
        alert('A Gold layer refresh is already in progress. Please wait for it to complete.');
      }
    } finally {
      setDashboardLoading(false);
      setTechnicianDataLoading(false);
      setRefreshing(false);
    }
  };

  const refreshEmployeeMaster = async () => {
    setRefreshing(true);
    try {
      setEmployeesLoading(true);
      
      await axios.post('/api/gold/refresh-employee-master');
      setRefreshInProgress(true);
      fetchData();
    } catch (error) {
      console.error('Error refreshing employee master:', error);
      if (error.response?.status === 409) {
        alert('A Gold layer refresh is already in progress. Please wait for it to complete.');
      }
    } finally {
      setEmployeesLoading(false);
      setRefreshing(false);
    }
  };

  const clearCache = async () => {
    try {
      await axios.post('/api/gold/cache/clear-all');
      alert('Cache cleared successfully! The page will refresh to show updated data.');
      window.location.reload();
    } catch (error) {
      console.error('Error clearing cache:', error);
      alert('Failed to clear cache. Please try again.');
    }
  };

  const updateGlobalFilter = (key, value) => {
    setGlobalFilters(prev => {
      const newFilters = {
        ...prev,
        [key]: value
      };
      
      // Save to localStorage whenever filters change
      try {
        localStorage.setItem('goldLayerFilters', JSON.stringify({
          date: newFilters.date.toISOString(),
          tenant: newFilters.tenant,
          work_location: newFilters.work_location,
          exclude_apprentices: newFilters.exclude_apprentices
        }));
      } catch (error) {
        console.error('Error saving filters to localStorage:', error);
      }
      
      return newFilters;
    });
  };

  // Function to clear saved filters
  const clearSavedFilters = () => {
    try {
      localStorage.removeItem('goldLayerFilters');
      setGlobalFilters({
        date: new Date(),
        tenant: '',
        work_location: '',
        exclude_apprentices: false
      });
    } catch (error) {
      console.error('Error clearing saved filters:', error);
    }
  };



  // Filter employees based on search term
  const filteredEmployees = employees.filter(employee => {
    if (!employeeSearch) return true;
    
    const searchTerm = employeeSearch.toLowerCase();
    const name = (employee.full_name || '').toLowerCase();
    const email = (employee.email || '').toLowerCase();
    const phone = (employee.phone || '').toLowerCase();
    const department = (employee.department || '').toLowerCase();
    const jobTitle = (employee.job_title || '').toLowerCase();
    const employeeId = (employee.employee_id || '').toString().toLowerCase();
    
    return name.includes(searchTerm) || 
           email.includes(searchTerm) || 
           phone.includes(searchTerm) ||
           department.includes(searchTerm) || 
           jobTitle.includes(searchTerm) ||
           employeeId.includes(searchTerm);
  });

  // New function to fetch detailed discrepancy information
  const fetchDiscrepancyDetail = async (discrepancyId) => {
    try {
      const response = await axios.get(`/api/gold/discrepancies/${discrepancyId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching discrepancy detail:', error);
      return null;
    }
  };

  // Fire-and-forget: record a technician view (idempotent on backend)
  const recordTechnicianView = async (employeeId, date, tenantId) => {
    try {
      const formattedDate = formatDateForAPI(date);
      // Do not await intentionally to avoid blocking UX; but we still catch errors
      axios.post('/api/gold/technician-views', {
        employee_id: employeeId,
        date: formattedDate,
        tenant_id: tenantId,
      }).catch(() => {});
    } catch (_) {
      // no-op; recording is best-effort
    }
  };

  // Reusable function to open detail modal for both discrepancies and technician summaries
  const openDetailModal = async (data, type = 'discrepancy') => {
    setDetailModalType(type);
    setSelectedDetailData(data);
    setShowDetailModal(true);
    
    if (type === 'discrepancy') {
      // Fetch additional details for individual discrepancy
      const detailedInfo = await fetchDiscrepancyDetail(data.id);
      if (detailedInfo) {
        setSelectedDetailData({ ...data, ...detailedInfo });
      }
    }
    // For technician type, we already have all the data we need
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedDetailData(null);
    setDetailModalType('discrepancy');
  };

  // Fetch detailed technician billing analysis
  const fetchTechnicianDetail = async (employeeId, date) => {
    try {
      const params = new URLSearchParams();
      const formattedDate = formatDateForAPI(date);
      params.append('date', formattedDate);
      
      const response = await axios.get(`/api/gold/technician-details/${employeeId}?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching technician detail:', error);
      return null;
    }
  };

  // Open comprehensive technician detail modal
  const openTechnicianDetailModal = async (technician) => {
    setSelectedTechnician(technician);
    const detailData = await fetchTechnicianDetail(technician.employee_id, globalFilters.date);
    if (detailData) {
      setTechnicianDetailData(detailData);
      setShowTechnicianDetailModal(true);
      // Best-effort recording of the view
      recordTechnicianView(technician.employee_id, globalFilters.date, technician.tenant_id);
    }
  };

  // Refresh just the view summary data without refetching everything
  const refreshViewSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (globalFilters.date) {
        const formattedDate = formatDateForAPI(globalFilters.date);
        params.append('date', formattedDate);
      }
      if (globalFilters.tenant) params.append('tenant', globalFilters.tenant);
      if (globalFilters.work_location) params.append('work_location', globalFilters.work_location);
      
      const viewsResponse = await axios.get(`/api/gold/technician-view-summary?${params}`);
      const views = viewsResponse.data.views || [];
      
      // Build a map of employee_id -> array of views
      const viewsByEmployee = views.reduce((acc, view) => {
        if (!acc[view.employee_id]) {
          acc[view.employee_id] = [];
        }
        acc[view.employee_id].push(view);
        return acc;
      }, {});
      
      // Update technician data with new view information
      setTechnicianData(prevData => 
        prevData.map(tech => ({
          ...tech,
          recent_views: viewsByEmployee[tech.employee_id] || []
        }))
      );
    } catch (error) {
      console.error('Error refreshing view summary (non-fatal):', error);
    }
  };

  const closeTechnicianDetailModal = () => {
    setShowTechnicianDetailModal(false);
    setTechnicianDetailData(null);
    setSelectedTechnician(null);
    // Refresh view summary to show the updated "Last Viewed" information
    refreshViewSummary();
  };

  // Helper function to format distance
  const formatDistance = (distanceFeet) => {
    if (distanceFeet == null) return 'N/A';
    
    const miles = distanceFeet / 5280;
    
    if (miles < 0.1) {
      return `${distanceFeet.toFixed(0)} ft`;
    }
    
    return `${miles.toFixed(2)} mi`;
  };

  // Helper function to format minutes to "3hr4m" format (extracted from discrepancies tab)
  const formatMinutesToHours = (minutes) => {
    if (!minutes || minutes === 0) return '0m';
    
    const isNegative = minutes < 0;
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    
    let result = '';
    if (hours === 0) {
      result = `${mins}m`;
    } else if (mins === 0) {
      result = `${hours}hr`;
    } else {
      result = `${hours}hr${mins}m`;
    }
    
    return isNegative ? `-${result}` : result;
  };


  // Add this helper function near the other formatting helpers
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString();
  };

  // Helper function to create a teaser from HTML content
  const createTeaser = (htmlContent, maxLength = 100) => {
    if (!htmlContent) return '';
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    // Get text content (strips HTML)
    let text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Clean up extra whitespace
    text = text.replace(/\s+/g, ' ').trim();
    
    // Create teaser
    if (text.length > maxLength) {
      return text.substring(0, maxLength).trim() + '...';
    }
    return text;
  };

  // Helper function to format time without leading zeros
  const formatTime = (date) => {
    if (!date) return '';
    
    // Convert string to Date object if needed
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    // Check if it's a valid date
    if (isNaN(dateObj.getTime())) return '';
    // Round to nearest minute (>= 30 seconds rounds up)
    const rounded = new Date(dateObj.getTime() + 30 * 1000);
    const hours = rounded.getHours();
    const minutes = rounded.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  // Helper function to format event time with date if different from scheduled
  const formatEventTime = (eventTime, scheduledDate) => {
    if (!eventTime) return 'N/A';
    const eventDate = new Date(eventTime);
    const scheduled = new Date(scheduledDate);
    
    // Compare dates (ignoring time)
    const isSameDay = eventDate.toDateString() === scheduled.toDateString();
    
    if (isSameDay) {
      return formatTime(eventDate).padEnd(8, ' ');
    } else {
      return `${formatTime(eventDate)}  (${eventDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' })})`;
    }
  };

  // Add this helper function near the other helpers
  const filterDiscrepancies = (discrepancies) => {
    if (!discrepancies) return [];
    return discrepancies.filter(d => 
      d.discrepancy_type === 'excessive_idle_time' || 
      d.discrepancy_type === 'gps_disabled' ||
      d.discrepancy_type === 'no_lunch_break'
    );
  };

  // Update the TechnicianDetailModal component to use filtered discrepancies
  const TechnicianDetailModal = ({ technicianData, onClose }) => {
    if (!technicianData) return null;

    const { employee_info, billing_analysis, adp_time_data, gps_tracking_data, vehicle_info, assignments } = technicianData;
    const filteredDiscrepancies = filterDiscrepancies(technicianData.discrepancies);
    


    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <div 
          className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  {employee_info.employee_name}
                  <span className="text-sm text-gray-500 ml-3 flex items-center">
                    <User className="h-4 w-4 mr-1 text-gray-600" />
                    {employee_info.employee_id}
                  </span>
                  {vehicle_info && vehicle_info.pairing_status === 'Unpaired' && (
                    <span className="text-sm text-red-600 ml-3 flex items-center font-semibold bg-red-50 px-2 py-1 rounded border border-red-200">
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      Unpaired Via Web
                    </span>
                  )}
                  {vehicle_info && vehicle_info.pairing_status === 'Paired' && (vehicle_info.vehicle_names || vehicle_info.vehicle_ids) && (
                    <span className="text-sm text-blue-600 ml-3 flex items-center">
                      <Truck className="h-4 w-4 mr-1" />
                      {vehicle_info.vehicle_names || vehicle_info.vehicle_ids}
                    </span>
                  )}
                  {vehicle_info && vehicle_info.pairing_status === 'Multiple Vehicles' && (
                    <span className="text-sm text-orange-600 ml-3 flex items-center font-semibold bg-orange-50 px-2 py-1 rounded border border-orange-200">
                      <Truck className="h-4 w-4 mr-1" />
                      Multiple Vehicles
                    </span>
                  )}
                  {(!vehicle_info || (!vehicle_info.vehicle_names && !vehicle_info.vehicle_ids && !vehicle_info.pairing_status)) && (
                    <span className="text-sm text-red-600 ml-3 flex items-center font-semibold bg-red-50 px-2 py-1 rounded border border-red-200">
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      No GPS Data
                    </span>
                  )}
                </h2>
                {/* Employee role and department info */}
                {(employee_info.department || employee_info.job_title) && (
                  <div className="flex items-center space-x-2 mt-1">
                    <p className="text-base font-medium text-gray-700">
                      {employee_info.tenant_location === 'DelAir RNC' && employee_info.work_location 
                        ? employee_info.work_location 
                        : employee_info.tenant_location}
                    </p>
                    <span className="text-gray-400">•</span>
                    {employee_info.department && (
                      <span className="py-2 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                        {employee_info.department}
                      </span>
                    )}
                    {employee_info.job_title && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                        {employee_info.job_title}
                      </span>
                    )}
                  </div>
                )}



              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-base font-medium text-gray-900">
                {(() => {
                  const date = new Date(employee_info.work_date + 'T12:00:00');
                  return date.toLocaleDateString([], {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                })()}
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="p-3 flex flex-col gap-3">
            {/* Billing Analysis - Only show if there's work time data */}
            {(() => {
              // Check if this is TFDA data by looking for shift structure in time_entries
              const isTfdaData = adp_time_data?.time_entries && 
                Array.isArray(adp_time_data.time_entries) && 
                adp_time_data.time_entries.length > 0 &&
                adp_time_data.time_entries.some(entry => 
                  entry && typeof entry === 'object' && 
                  ('shift_number' in entry || 'shift_start' in entry || 'shift_end' in entry)
                );

              // Only show billing analysis if there's actual work time data
              const hasWorkTime = billing_analysis.adp_paid_minutes > 0;
              
              if (!hasWorkTime) return null;

              const timeLabel = isTfdaData ? 'TFDA Work Time' : 'ADP Work Time';

              return (
                <div className="bg-yellow-50 rounded-lg px-3 py-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center">
                    <DollarSign className="h-5 w-5 mr-2 text-yellow-500" />
                    Billing Analysis
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-4 mb-4">
                    <div className="bg-white p-3 rounded border">
                      <p className="text-sm text-gray-600">{timeLabel}</p>
                      <p className="font-bold text-lg text-blue-600">
                        {formatMinutesToHours(billing_analysis.adp_paid_minutes)}
                      </p>
                      <p className="text-xs text-gray-500">
                        ({billing_analysis.adp_paid_minutes} min)
                      </p>
                    </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Actual Work Time</p>
                  <p className="font-bold text-lg text-green-600">
                    {formatMinutesToHours(billing_analysis.actual_work_minutes)}
                  </p>
                  <p className="text-xs text-gray-500">
                    ({billing_analysis.actual_work_minutes} min)
                  </p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm text-gray-600">Variance</p>
                  <div>
                    <p className={`font-bold text-lg ${
                      billing_analysis.variance_minutes === 0 ? 'text-green-600' :
                      billing_analysis.variance_minutes > 0 ? (
                        Math.abs(billing_analysis.variance_minutes) > 300 ? 'text-red-600' :
                        Math.abs(billing_analysis.variance_minutes) > 180 ? 'text-orange-600' :
                        Math.abs(billing_analysis.variance_minutes) > 60 ? 'text-yellow-600' : 'text-yellow-500'
                      ) : (
                        Math.abs(billing_analysis.variance_minutes) > 300 ? 'text-red-600' :
                        Math.abs(billing_analysis.variance_minutes) > 180 ? 'text-orange-600' :
                        Math.abs(billing_analysis.variance_minutes) > 60 ? 'text-yellow-600' : 'text-blue-600'
                      )
                    }`}>
                      {billing_analysis.variance_minutes > 0 ? '+' : ''}{formatMinutesToHours(Math.abs(billing_analysis.variance_minutes))}

                    </p>
                    {(() => {
                      // Calculate paid drive time for net variance display
                      const eveningCommute = gps_tracking_data?.commute_data?.evening_commute;
                      const isDoorToDoorDay = gps_tracking_data?.commute_data?.is_door_to_door_day;
                      
                      // Use generalized function for morning drive time
                      const morningPaidDriveTime = calculateMorningDriveTime({ gps_tracking_data }, assignments, employee_info);
                      const morningDriveTime = isDoorToDoorDay ? morningPaidDriveTime : (morningPaidDriveTime + 45);
                      
                      // Check if end_of_day is an office arrival WITH NO DEPARTURE (if so, don't include evening drive time)
                      const isEndOfDayOfficeArrivalWithNoDeparture = (() => {
                        if (!billing_analysis?.end_of_day || !gps_tracking_data.location_arrivals) {
                          return false;
                        }
                        
                        try {
                          const endOfDayTime = new Date(billing_analysis.end_of_day).getTime();
                          const locationData = typeof gps_tracking_data.location_arrivals === 'string' 
                            ? JSON.parse(gps_tracking_data.location_arrivals) 
                            : gps_tracking_data.location_arrivals;
                          
                          if (locationData && locationData.all_visits && Array.isArray(locationData.all_visits)) {
                            return locationData.all_visits.some(visit => {
                              if (visit.location_type === 'office' && visit.arrival_time) {
                                const arrivalTime = new Date(visit.arrival_time).getTime();
                                const timeDiff = Math.abs(endOfDayTime - arrivalTime);
                                
                                // Check if end_of_day matches this office arrival (within 5 minutes)
                                if (timeDiff <= 300000) {
                                  // Only exclude evening drive time if there's NO departure time (they stayed at office)
                                  return !visit.departure_time || visit.departure_time === null;
                                }
                              }
                              return false;
                            });
                          }
                        } catch (error) {
                          console.error('Error checking end_of_day office arrival with no departure:', error);
                        }
                        return false;
                      })();
                      
                      // Use the same logic as display: lesser of Azuga vs Google time
                      const baseEveningDriveTime = (() => {
                        if (isEndOfDayOfficeArrivalWithNoDeparture) return 0;
                        const eveningAzugaTime = eveningCommute?.minutes || 0;
                        const eveningGoogleTime = eveningCommute?.expected_duration_minutes || 0;
                        return Math.min(eveningAzugaTime, eveningGoogleTime);
                      })();
                      const eveningDriveTime = baseEveningDriveTime;
                      
                      // For door-to-door days (Sundays/holidays), don't subtract 45 minutes
                      const eveningPaidDriveTime = isDoorToDoorDay ? eveningDriveTime : Math.max(0, eveningDriveTime - 45);
                      
                      // Cap drive time adjustments by individual penalty periods
                      const morningPenalty = billing_analysis.start_of_day_penalty_minutes || 0;
                      const eveningPenalty = billing_analysis.end_of_day_penalty_minutes || 0;
                      
                      const cappedMorningAdjustment = Math.min(morningPaidDriveTime, morningPenalty);
                      const cappedEveningAdjustment = Math.min(eveningPaidDriveTime, eveningPenalty);
                      const totalCappedAdjustment = cappedMorningAdjustment + cappedEveningAdjustment;
                      
                      const netVariance = billing_analysis.variance_minutes - totalCappedAdjustment;
                      
                      console.log('DEBUG Variance Calculation:', {
                        variance_minutes: billing_analysis.variance_minutes,
                        morningDriveTime: morningDriveTime,
                        eveningDriveTime: eveningDriveTime,
                        morningPaidDriveTime: morningPaidDriveTime,
                        eveningPaidDriveTime: eveningPaidDriveTime,
                        morningPenalty: morningPenalty,
                        eveningPenalty: eveningPenalty,
                        cappedMorningAdjustment: cappedMorningAdjustment,
                        cappedEveningAdjustment: cappedEveningAdjustment,
                        totalCappedAdjustment: totalCappedAdjustment,
                        netVariance: netVariance,
                        isDoorToDoorDay: isDoorToDoorDay
                      });
                      
                      // On door-to-door days, don't show drive time adjustment since drive time is already included
                      if (isDoorToDoorDay) {
                        return null;
                      }
                      
                      // Only show net variance if it's different from the regular variance
                      if (netVariance === billing_analysis.variance_minutes) {
                        console.log('DEBUG: Not showing variance because netVariance equals original variance');
                        return null;
                      }
                      
                      return (
                        <p className="">
                          <span className={`text-medium font-medium ${
                            netVariance === 0 ? 'text-green-600' :
                            netVariance > 0 ? (
                              Math.abs(netVariance) > 300 ? 'text-red-600' :
                              Math.abs(netVariance) > 180 ? 'text-orange-600' :
                              Math.abs(netVariance) > 60 ? 'text-yellow-600' : 'text-yellow-500'
                            ) : 'text-green-600'
                          }`}>
                            {netVariance > 0 ? '+' : netVariance < 0 ? '-' : ''}{formatMinutesToHours(Math.abs(netVariance))}
                          </span>
                          <span className="text-sm text-gray-600">
                            {netVariance < 0 ? ' underbilled w/drive time' : netVariance >= 0 ? ' with drive time' : ''}
                          </span>
                        </p>
                      );
                    })()}
                  </div>
                </div>
              </div>
              

                </div>
              );
            })()}

            {/* Day Overview - Start and End Times */}
            <div className="bg-indigo-50 rounded-lg p-3">
              <h3 className="text-lg font-medium text-gray-900 mb-2 flex items-center">
                <Clock className="h-5 w-5 mr-2 text-indigo-500" />
                Day Overview
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Start of Day */}
                <div className="bg-white rounded-lg p-4 border border-indigo-200">
                  <h4 className="font-medium text-indigo-800 mb-3 flex items-center">
                    Start of Day
                    {billing_analysis?.start_of_day_penalty_minutes > 0 && (
                      <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 rounded text-sm">
                        {formatMinutesToHours(billing_analysis.start_of_day_penalty_minutes)}
                      </span>
                    )}
                    {(() => {
                      // Use generalized function for morning drive time

                      const morningPaidDriveTime = calculateMorningDriveTime({ gps_tracking_data }, assignments, employee_info);
                      
                      console.log('🚗 MODAL DRIVE TIME:', {
                        employee_id: gps_tracking_data?.employee_id,
                        tenant_code: employee_info?.tenant_code,
                        tenant_location: employee_info?.tenant_location,
                        is_sarasota: employee_info?.tenant_code === 'L107',
                        morningPaidDriveTime
                      });
                      
                      return morningPaidDriveTime > 0 && (
                        <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 rounded text-sm flex items-center">
                          🚗 {formatMinutesToHours(morningPaidDriveTime)} paid drive
                        </span>
                      );
                    })()}

                  </h4>
                  <div className="space-y-3">
                    {(() => {
                      // Collect all events with their timestamps
                      const events = [];

                      // Calculate first job time for filtering (do this first!)
                      let firstJobTime = null;
                      if (gps_tracking_data.first_job_gps_arrival) {
                        firstJobTime = new Date(gps_tracking_data.first_job_gps_arrival);
                      } else if (gps_tracking_data.first_job_st_arrival) {
                        firstJobTime = new Date(gps_tracking_data.first_job_st_arrival);
                      } else if (assignments && assignments.length > 0 && assignments[0].st_dispatched_on) {
                        firstJobTime = new Date(assignments[0].st_dispatched_on);
                      }

                      // Morning Meetings (ADP entries before first Regular/Overtime)
                      // Handle both legacy format (array) and new format (object with adp_entries/tfda_shifts)
                      let timeEntries = [];
                      if (Array.isArray(adp_time_data.time_entries)) {
                        // Check if this is ADP data (has entryTotals) or TFDA data (has shift_number)
                        if (adp_time_data.time_entries.length > 0 && adp_time_data.time_entries[0].shift_number !== undefined) {
                          // This is TFDA data, not ADP - skip ADP processing
                          timeEntries = [];
                        } else {
                          // Legacy ADP format: direct array
                          timeEntries = adp_time_data.time_entries;
                        }
                      } else if (adp_time_data.time_entries && adp_time_data.time_entries.adp_entries) {
                        // New format: object with adp_entries and tfda_shifts
                        timeEntries = adp_time_data.time_entries.adp_entries || [];
                      }
                      const filteredTimeEntries = timeEntries.filter(entry => 
                        entry.entryTotals?.[0]?.payCode?.codeValue !== 'HOLIDAY'
                      );
                      const firstRegularIndex = filteredTimeEntries.findIndex(entry => 
                        entry.entryTotals?.[0]?.payCode?.codeValue === 'REGULAR' || 
                        entry.entryTotals?.[0]?.payCode?.codeValue === 'OVERTIME'
                      );
                      
                      const morningMeetings = firstRegularIndex > 0 ? filteredTimeEntries.slice(0, firstRegularIndex) : [];
                      morningMeetings.forEach((meeting, index) => {
                        if (meeting.startPeriod?.startDateTime) {
                          events.push({
                            time: new Date(meeting.startPeriod.startDateTime),
                            type: 'morning_meeting',
                            label: `ADP Punch In ${morningMeetings.length > 1 ? `${index + 1} ` : ''}(${meeting.entryTotals?.[0]?.payCode?.codeValue || 'MET'})`,
                            value: `${formatTime(new Date(meeting.startPeriod.startDateTime))} - ${meeting.endPeriod?.endDateTime ? formatTime(new Date(meeting.endPeriod.endDateTime)) : 'N/A'}`,
                            colorClass: 'text-gray-400'
                          });
                        }
                      });

                      // ADP Punch In (work punch)
                      const firstWorkEntry = filteredTimeEntries.find(entry => 
                        entry.entryTotals?.[0]?.payCode?.codeValue === 'REGULAR' || 
                        entry.entryTotals?.[0]?.payCode?.codeValue === 'OVERTIME'
                      );
                      
                      if (firstWorkEntry?.startPeriod?.startDateTime) {
                        const punchInTime = new Date(firstWorkEntry.startPeriod.startDateTime);
                        const expectedStartTime = billing_analysis?.start_of_day ? new Date(billing_analysis.start_of_day) : null;
                        
                        // Determine color based on timing relative to expected start
                        let punchInColor = 'text-green-700'; // default green
                        if (expectedStartTime) {
                          if (punchInTime < expectedStartTime) {
                            punchInColor = 'text-red-700'; // red if before expected start (early)
                          } else {
                            punchInColor = 'text-green-700'; // green if after or equal to expected start (on time or late)
                          }
                        }
                        
                        events.push({
                          time: punchInTime,
                          type: 'punch_in',
                          label: 'ADP Punch In',
                          value: formatTime(punchInTime),
                          colorClass: punchInColor,
                          isAdpPunch: true
                        });
                      }

                      // Check if billing_analysis.start_of_day matches the tenant's minimum start time
                      // If so, add a separate minimum start time event
                      if (employee_info?.minimum_start_time && billing_analysis?.start_of_day) {
                        // Parse minimum start time (which is in Eastern time format "HH:MM:SS")
                        const [hours, minutes, seconds] = employee_info.minimum_start_time.split(':');
                        
                        // Get the work date
                        const workDateStr = employee_info?.work_date;
                        const workDate = new Date(workDateStr);
                        
                        // Calculate UTC time from Eastern time
                        // During DST (March-November): Eastern = UTC - 4 hours
                        // During Standard (November-March): Eastern = UTC - 5 hours
                        const minimumStartUTC_DST = new Date(workDate);
                        minimumStartUTC_DST.setUTCHours(parseInt(hours) + 4, parseInt(minutes), parseInt(seconds || 0), 0);
                        
                        const minimumStartUTC_STD = new Date(workDate);
                        minimumStartUTC_STD.setUTCHours(parseInt(hours) + 5, parseInt(minutes), parseInt(seconds || 0), 0);
                        
                        const startOfDayTime = new Date(billing_analysis.start_of_day);
                        
                        // Check if billing_analysis.start_of_day matches minimum start time (within 30 seconds) for either timezone
                        const timeDiff_DST = Math.abs(startOfDayTime.getTime() - minimumStartUTC_DST.getTime());
                        const timeDiff_STD = Math.abs(startOfDayTime.getTime() - minimumStartUTC_STD.getTime());
                        
                        const isMinimumStartTime = timeDiff_DST <= 30 * 1000 || timeDiff_STD <= 30 * 1000;
                        
                        if (isMinimumStartTime) {
                          console.log('🕐 Adding Minimum Start Time Event:', {
                            minimum_start_time: employee_info.minimum_start_time,
                            start_of_day: billing_analysis.start_of_day,
                            minimumStartUTC_DST: minimumStartUTC_DST.toISOString(),
                            minimumStartUTC_STD: minimumStartUTC_STD.toISOString(),
                            timeDiff_DST: timeDiff_DST / 1000,
                            timeDiff_STD: timeDiff_STD / 1000
                          });
                          
                          events.push({
                            time: startOfDayTime,
                            type: 'minimum_start_time',
                            label: 'Minimum Start Time',
                            value: formatTime(startOfDayTime),
                            colorClass: 'text-blue-700',
                            isMinimumStartTime: true
                          });
                        }
                      }

                      // TFDA Shift Start
                      // Handle TFDA shifts from time_entries - support both formats
                      let tfdaShifts = [];
                      if (adp_time_data.time_entries) {
                        if (Array.isArray(adp_time_data.time_entries)) {
                          // TFDA-only format: direct array of shifts
                          // Check if this is TFDA data (has shift_number) vs ADP data (has entryTotals)
                          if (adp_time_data.time_entries.length > 0 && adp_time_data.time_entries[0].shift_number !== undefined) {
                            tfdaShifts = adp_time_data.time_entries;
                          }
                        } else if (adp_time_data.time_entries.tfda_shifts) {
                          // Merged format: object with tfda_shifts property
                          tfdaShifts = adp_time_data.time_entries.tfda_shifts;
                        }
                      }
                      
                      // Find earliest TFDA shift start
                      if (tfdaShifts.length > 0) {
                        const earliestShift = tfdaShifts.reduce((earliest, current) => {
                          if (!current.shift_start) return earliest;
                          if (!earliest.shift_start) return current;
                          return new Date(current.shift_start) < new Date(earliest.shift_start) ? current : earliest;
                        }, tfdaShifts[0]);
                        
                        if (earliestShift?.shift_start) {
                          const tfdaStartTime = new Date(earliestShift.shift_start);
                          const expectedStartTime = billing_analysis?.start_of_day ? new Date(billing_analysis.start_of_day) : null;
                          
                          // Determine color based on timing relative to expected start
                          let tfdaStartColor = 'text-green-700'; // default green
                          if (expectedStartTime) {
                            if (tfdaStartTime < expectedStartTime) {
                              tfdaStartColor = 'text-red-700'; // red if before expected start (early)
                            } else {
                              tfdaStartColor = 'text-green-700'; // green if after or equal to expected start (on time or late)
                            }
                          }
                          
                          events.push({
                            time: tfdaStartTime,
                            type: 'tfda_start',
                            label: 'TFDA Shift Start',
                            value: formatTime(tfdaStartTime),
                            colorClass: tfdaStartColor,
                            isTfdaShift: true
                          });
                        }
                      }

                      // Location Arrivals (only before first job arrival)
                      if (gps_tracking_data) {
                        // Add office arrivals from comprehensive visit data
                        if (gps_tracking_data.location_arrivals) {
                          try {
                            const locationData = typeof gps_tracking_data.location_arrivals === 'string' 
                              ? JSON.parse(gps_tracking_data.location_arrivals) 
                              : gps_tracking_data.location_arrivals;
                            
                            if (locationData && locationData.all_visits && Array.isArray(locationData.all_visits)) {
                              locationData.all_visits.forEach(visit => {
                                if (visit.location_type === 'office') {
                                  // Add office arrival
                                  if (visit.arrival_time) {
                                    const officeArrivalTime = new Date(visit.arrival_time);
                                    if (!firstJobTime || officeArrivalTime < firstJobTime) {
                                      events.push({
                                        time: officeArrivalTime,
                                        type: 'location_arrival',
                                        label: `${visit.location_name || 'Office'} Arrival`,
                                        value: formatTime(officeArrivalTime),
                                        colorClass: 'text-blue-700',
                                        locationType: 'office',
                                        locationName: visit.location_name,
                                        duration: visit.duration_minutes,
                                        departureTime: visit.departure_time
                                      });
                                    }
                                  }
                                  // Add office departure (if not still inside and before first job)
                                  if (visit.departure_time && !visit.still_inside) {
                                    const officeDepartureTime = new Date(visit.departure_time);
                                    if (!firstJobTime || officeDepartureTime < firstJobTime) {
                                      events.push({
                                        time: officeDepartureTime,
                                        type: 'location_departure',
                                        label: `${visit.location_name || 'Office'} Departure`,
                                        value: formatTime(officeDepartureTime),
                                        colorClass: 'text-orange-700',
                                        locationType: 'office',
                                        locationName: visit.location_name,
                                        duration: visit.duration_minutes,
                                        arrivalTime: visit.arrival_time
                                      });
                                    }
                                  }
                                }
                              });
                            }
                          } catch (error) {
                            console.error('Error parsing office arrivals from location_arrivals:', error);
                          }
                        }
                        
                        
                        // Add first warehouse arrival
                        if (gps_tracking_data.first_warehouse_arrival) {
                          const warehouseArrivalTime = new Date(gps_tracking_data.first_warehouse_arrival);
                          if (!firstJobTime || warehouseArrivalTime < firstJobTime) {
                            events.push({
                              time: warehouseArrivalTime,
                              type: 'location_arrival',
                              label: 'Warehouse Arrival',
                              value: formatTime(warehouseArrivalTime),
                              colorClass: 'text-orange-700',
                              locationType: 'warehouse'
                            });
                          }
                        }
                        
                        // Add first other location arrival
                        if (gps_tracking_data.first_other_location_arrival) {
                          const otherArrivalTime = new Date(gps_tracking_data.first_other_location_arrival);
                          if (!firstJobTime || otherArrivalTime < firstJobTime) {
                            events.push({
                              time: otherArrivalTime,
                              type: 'location_arrival',
                              label: 'Other Location Arrival',
                              value: formatTime(otherArrivalTime),
                              colorClass: 'text-purple-700',
                              locationType: 'other'
                            });
                          }
                        }
                      }
                      // Add detailed location arrivals (all visits)
                      if (gps_tracking_data.location_arrivals) {
                        try {
                          const locationData = typeof gps_tracking_data.location_arrivals === 'string' 
                            ? JSON.parse(gps_tracking_data.location_arrivals) 
                            : gps_tracking_data.location_arrivals;
                          
                          // Handle new comprehensive visit data structure - show non-office visits only (office visits handled above)
                          if (locationData && locationData.all_visits && Array.isArray(locationData.all_visits)) {
                            locationData.all_visits.forEach(visit => {
                              // Skip office visits as they're handled in the main location arrivals section above
                              if (visit.location_type !== 'office') {
                                // Add arrival event
                                if (visit.arrival_time) {
                                  const arrivalTime = new Date(visit.arrival_time);
                                  if (!firstJobTime || arrivalTime < firstJobTime) {
                                    events.push({
                                      time: arrivalTime,
                                      type: 'location_arrival_detail',
                                      label: `${visit.location_name || 'Unknown Location'} Arrival`,
                                      value: formatTime(arrivalTime),
                                      colorClass: 'text-indigo-700',
                                      locationType: visit.location_type || 'other',
                                      locationName: visit.location_name,
                                      duration: visit.duration_minutes,
                                      departureTime: visit.departure_time
                                    });
                                  }
                                }
                                // Add departure event (if not still inside and before first job)
                                if (visit.departure_time && !visit.still_inside) {
                                  const departureTime = new Date(visit.departure_time);
                                  if (!firstJobTime || departureTime < firstJobTime) {
                                    events.push({
                                      time: departureTime,
                                      type: 'location_departure_detail',
                                      label: `${visit.location_name || 'Unknown Location'} Departure`,
                                      value: formatTime(departureTime),
                                      colorClass: 'text-orange-700',
                                      locationType: visit.location_type || 'other',
                                      locationName: visit.location_name,
                                      duration: visit.duration_minutes,
                                      arrivalTime: visit.arrival_time
                                    });
                                  }
                                }
                              }
                            });
                          }
                          // Fallback for old array format
                          else if (Array.isArray(locationData)) {
                            locationData.forEach(arrival => {
                              if (arrival.arrival_time) {
                                const arrivalTime = new Date(arrival.arrival_time);
                                if (!firstJobTime || arrivalTime < firstJobTime) {
                                  events.push({
                                    time: arrivalTime,
                                    type: 'location_arrival_detail',
                                    label: arrival.location_name || 'Unknown Location',
                                    value: formatTime(arrivalTime),
                                    colorClass: 'text-indigo-700',
                                    locationType: arrival.location_type || 'other',
                                    locationName: arrival.location_name
                                  });
                                }
                              }
                            });
                          }
                        } catch (error) {
                          console.error('Error parsing location arrivals:', error);
                        }
                      }

                      // Note: Office departures now handled in comprehensive visit data above
                      
                      if (gps_tracking_data) {
                        
                        // Add warehouse departure if it falls before first job
                        if (gps_tracking_data.last_warehouse_departure) {
                          const warehouseDepartureTime = new Date(gps_tracking_data.last_warehouse_departure);
                          if (!firstJobTime || warehouseDepartureTime < firstJobTime) {
                            events.push({
                              time: warehouseDepartureTime,
                              type: 'location_departure',
                              label: 'Warehouse Departure',
                              value: formatTime(warehouseDepartureTime),
                              colorClass: 'text-orange-600',
                              locationType: 'warehouse'
                            });
                          }
                        }
                        
                        // Add other location departure if it falls before first job
                        if (gps_tracking_data.last_other_location_departure) {
                          const otherDepartureTime = new Date(gps_tracking_data.last_other_location_departure);
                          if (!firstJobTime || otherDepartureTime < firstJobTime) {
                            events.push({
                              time: otherDepartureTime,
                              type: 'location_departure',
                              label: 'Other Location Departure',
                              value: formatTime(otherDepartureTime),
                              colorClass: 'text-purple-600',
                              locationType: 'other'
                            });
                          }
                        }
                      }

                      // Note: location_departures processing removed - all departure data is now in location_arrivals

                      // Non-job appointments
                      const travelAppointments = technicianData.non_job_appointments?.travel_appointments || [];
                      const nonTravelAppointments = technicianData.non_job_appointments?.non_travel_appointments || [];
                      const allAppointments = [
                        ...travelAppointments.map(a => ({...a, travel: true})),
                        ...nonTravelAppointments.map(a => ({...a, travel: false}))
                      ];

                      allAppointments.forEach(appointment => {
                        if (appointment.start_time) {
                          const apptTime = new Date(appointment.start_time);
                          if (!firstJobTime || apptTime < firstJobTime) {
                            events.push({
                              time: apptTime,
                              type: appointment.travel ? 'travel_appointment' : 'virtual_appointment',
                              label: appointment.name || 'Appointment',
                              value: `${formatTime(apptTime)}${appointment.duration ? ` - ${appointment.duration} min` : ''}`,
                              colorClass: appointment.travel ? 'text-purple-600' : 'text-blue-400',
                              travel: appointment.travel
                            });
                          }
                        }
                      });

                      // First job events
                      if (assignments && assignments.length > 0) {
                        const firstAssignment = assignments[0];
                        if (firstAssignment.st_dispatched_on) {
                          events.push({
                            time: new Date(firstAssignment.st_dispatched_on),
                            type: 'dispatched',
                            label: 'Dispatched (First Job)',
                            value: formatTime(new Date(firstAssignment.st_dispatched_on)),
                            colorClass: 'text-purple-700'
                          });
                        }
                      }

                      if (gps_tracking_data.first_job_gps_arrival) {
                        events.push({
                          time: new Date(gps_tracking_data.first_job_gps_arrival),
                          type: 'gps_arrival',
                          label: 'Azuga Arrival (First Job)',
                          value: formatTime(new Date(gps_tracking_data.first_job_gps_arrival)),
                          colorClass: 'text-blue-700'
                        });
                      }



                      if (gps_tracking_data.first_job_st_arrival) {
                        events.push({
                          time: new Date(gps_tracking_data.first_job_st_arrival),
                          type: 'st_arrival',
                          label: 'ServiceTitan Arrival (First Job)',
                          value: formatTime(new Date(gps_tracking_data.first_job_st_arrival)),
                          colorClass: 'text-gray-700'
                        });
                      }

                      // Sort all events by time
                      events.sort((a, b) => a.time.getTime() - b.time.getTime());

                      // Highlight events that match the expected start/end of day times
                      const expectedStartTime = billing_analysis?.start_of_day ? new Date(billing_analysis.start_of_day) : null;
                      const expectedEndTime = billing_analysis?.end_of_day ? new Date(billing_analysis.end_of_day) : null;

                      // Add highlighting to events that match expected times exactly
                      events.forEach(event => {
                        if (expectedStartTime && Math.abs(event.time.getTime() - expectedStartTime.getTime()) <= 30 * 1000) { // 30 seconds tolerance
                          event.isExpectedStart = true;
                        }
                        if (expectedEndTime && Math.abs(event.time.getTime() - expectedEndTime.getTime()) <= 30 * 1000) { // 30 seconds tolerance
                          event.isExpectedEnd = true;
                        }
                      });

                      // Add events without timestamps at the end
                      const eventsWithoutTime = [];

                      // Check for missing required events
                      // Note: We no longer show "ADP Punch In - Not available" entries - just skip them
                      // if (!events.some(e => e.type === 'punch_in')) {
                      //   eventsWithoutTime.push({
                      //     type: 'punch_in',
                      //     label: 'ADP Punch In',
                      //     value: 'Not available',
                      //     colorClass: 'text-green-700'
                      //   });
                      // }

                      if (!events.some(e => e.type === 'dispatched')) {
                        eventsWithoutTime.push({
                          type: 'dispatched',
                          label: 'Dispatched (First Job)',
                          value: 'Not available',
                          colorClass: 'text-purple-700'
                        });
                      }

                      if (!events.some(e => e.type === 'gps_arrival')) {
                        eventsWithoutTime.push({
                          type: 'gps_arrival',
                          label: 'Azuga Arrival (First Job)',
                          value: 'Not available',
                          colorClass: 'text-blue-700'
                        });
                      }

                      if (!events.some(e => e.type === 'st_arrival')) {
                        eventsWithoutTime.push({
                          type: 'st_arrival',
                          label: 'ServiceTitan Arrival (First Job)',
                          value: 'Not available',
                          colorClass: 'text-gray-700'
                        });
                      }
                      
                      return (
                              <div>
                                {[...events, ...eventsWithoutTime].map((event, index) => (
                                  <div key={`${event.type}-${index}`} className={`flex justify-between items-center ${
                                    event.isMinimumStartTime ? 'bg-blue-50 border-l-4 border-blue-500 pl-2' :
                                    event.isAdpPunch ? (event.colorClass === 'text-red-700' ? 'bg-red-50 border-l-4 border-red-500 pl-2' : 'bg-green-50 border-l-4 border-green-500 pl-2') :
                                    event.isTfdaShift ? (event.colorClass === 'text-red-700' ? 'bg-red-50 border-l-4 border-red-500 pl-2' : 'bg-green-50 border-l-4 border-green-500 pl-2') :
                                    event.isExpectedStart ? 'bg-blue-50 border-l-4 border-blue-600 pl-2' :
                                    event.isExpectedEnd ? 'bg-blue-50 border-l-4 border-blue-600 pl-2' :
                                    ''
                                  }`}>
                                    <span className="text-sm text-gray-600 flex items-center">
                                      {event.label}
                                      {event.type === 'travel_appointment' && (
                                        <Truck className="h-4 w-4 ml-2 text-purple-500" />
                                      )}
                                      {event.type === 'virtual_appointment' && (
                                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Virtual/Remote</span>
                                      )}
                                      {event.type === 'location_arrival_detail' && (
                                        <span className={`ml-2 px-2 py-0.5 rounded text-xs ${
                                          event.locationType === 'office' ? 'bg-blue-100 text-blue-700' :
                                          event.locationType === 'vendor' ? 'bg-green-100 text-green-700' :
                                          event.locationType === 'warehouse' ? 'bg-orange-100 text-orange-700' :
                                          'bg-purple-100 text-purple-700'
                                        }`}>
                                          {event.locationName ? 'Visit' : 'Location'}
                                        </span>
                                      )}
                                    </span>
                                    <span className={`font-medium ${event.colorClass}`}>
                                      {event.value}
                                    </span>
                                  </div>
                                ))}

                                {/* Morning Commute Details */}
                                {gps_tracking_data.commute_data?.morning_commute && (() => {
                                  const morningCommute = gps_tracking_data.commute_data.morning_commute;
                                  const details = [];
                                  
                                  // Commute time (start - end)
                                  if (morningCommute.start_time && morningCommute.end_time) {
                                    details.push({
                                      label: 'Commute Time',
                                      value: `${formatTime(new Date(morningCommute.start_time))} - ${formatTime(new Date(morningCommute.end_time))}`,
                                      colorClass: 'text-purple-700 font-bold'
                                    });
                                  }
                                  
                                  // Address flow (start → end)
                                  if (morningCommute.start_address && morningCommute.end_address) {
                                    details.push({
                                      label: '',
                                      value: (
                                        <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-blue-200">
                                          <div className="flex items-center text-sm text-gray-700 mb-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                            <div className="truncate">
                                              {(() => {
                                                const breadcrumbs = morningCommute.breadcrumbs || [];
                                                const startLat = morningCommute.start_latitude ?? (breadcrumbs[0]?.latitude);
                                                const startLon = morningCommute.start_longitude ?? (breadcrumbs[0]?.longitude);
                                                const startHref = (startLat != null && startLon != null)
                                                  ? `https://www.google.com/maps?q=${startLat},${startLon}`
                                                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(morningCommute.start_address)}`;
                                                return (
                                                  <a
                                                    href={startHref}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 underline"
                                                  >
                                                    {morningCommute.start_address}
                                                  </a>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                          <div className="flex items-center text-sm text-gray-700">
                                            <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                                            <div className="truncate">
                                              {(() => {
                                                const breadcrumbs = morningCommute.breadcrumbs || [];
                                                const endLat = morningCommute.end_latitude ?? (breadcrumbs[breadcrumbs.length - 1]?.latitude);
                                                const endLon = morningCommute.end_longitude ?? (breadcrumbs[breadcrumbs.length - 1]?.longitude);
                                                const endHref = (endLat != null && endLon != null)
                                                  ? `https://www.google.com/maps?q=${endLat},${endLon}`
                                                  : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(morningCommute.end_address)}`;
                                                return (
                                                  <a
                                                    href={endHref}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-800 underline"
                                                  >
                                                    {morningCommute.end_address}
                                                  </a>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        </div>
                                      ),
                                      colorClass: 'w-full',
                                      spacingClass: 'mt-2'
                                    });
                                  }
                                  
                                  // Determine which time is lower for styling
                                  const azugaTime = morningCommute?.minutes || 0;
                                  const googleTime = morningCommute?.expected_duration_minutes || 0;
                                  const isGoogleLower = googleTime > 0 && azugaTime > 0 ? googleTime <= azugaTime : googleTime > 0;
                                  
                                  // Google Route duration (styled based on whether it's being used)
                                  if (morningCommute.expected_duration_minutes) {
                                    details.push({
                                      label: 'Commute Duration',
                                      value: (
                                        <span className="flex items-center">
                                          <span className="text-xs text-gray-500 mr-1">Google Route</span>
                                          <Route className="h-4 w-4 mr-1" />
                                          {formatMinutesToHours(morningCommute.expected_duration_minutes)}
                                        </span>
                                      ),
                                      colorClass: isGoogleLower ? 'text-green-700 font-bold' : 'text-gray-600 text-sm italic',
                                      spacingClass: 'mt-3'
                                    });
                                  }
                                  
                                  // Azuga duration (styled based on whether it's being used)
                                  if (morningCommute.minutes) {
                                    details.push({
                                      label: '',
                                      value: (
                                        <span className="flex items-center">
                                          <span className="text-xs text-gray-500 mr-1">Azuga</span>
                                          <Truck className="h-4 w-4 mr-1" />
                                          {formatMinutesToHours(morningCommute.minutes)}
                                        </span>
                                      ),
                                      colorClass: !isGoogleLower ? 'text-green-700 font-bold' : 'text-gray-600 text-sm italic',
                                      spacingClass: 'mb-2'
                                    });
                                  }
                                  
                                  // Google Route distance (styled based on duration comparison)
                                  if (morningCommute.expected_distance_miles) {
                                    details.push({
                                      label: 'Commute Distance',
                                      value: (
                                        <span className="flex items-center">
                                          <span className="text-xs text-gray-500 mr-1">Google Route</span>
                                          <Route className="h-4 w-4 mr-1" />
                                          {morningCommute.expected_distance_miles.toFixed(2)} miles
                                        </span>
                                      ),
                                      colorClass: isGoogleLower ? 'text-blue-700 font-bold' : 'text-gray-600 text-sm italic'
                                    });
                                  }
                                  
                                  // Azuga distance (styled based on duration comparison)
                                  if (morningCommute.miles) {
                                    details.push({
                                      label: '',
                                      value: (
                                        <span className="flex items-center">
                                          <span className="text-xs text-gray-500 mr-1">Azuga</span>
                                          <Truck className="h-4 w-4 mr-1" />
                                          {morningCommute.miles.toFixed(2)} miles
                                        </span>
                                      ),
                                      colorClass: !isGoogleLower ? 'text-blue-700 font-bold' : 'text-gray-600 text-sm italic'
                                    });
                                  }
                                  
                                  // Individual trip details
                                  if (morningCommute.breadcrumbs && morningCommute.breadcrumbs.length > 0) {
                                    // Add spacing before trips
                                    details.push({
                                      label: '',
                                      value: '',
                                      colorClass: 'mt-2'
                                    });
                                    
                                    // Group breadcrumbs by trip_number
                                    const tripGroups = {};
                                    morningCommute.breadcrumbs.forEach(breadcrumb => {
                                      const tripNum = breadcrumb.trip_number;
                                      if (tripNum) {
                                        if (!tripGroups[tripNum]) {
                                          tripGroups[tripNum] = [];
                                        }
                                        tripGroups[tripNum].push(breadcrumb);
                                      }
                                    });
                                    
                                    // Add trip details for each trip
                                    Object.keys(tripGroups).forEach((tripNumber, index) => {
                                      const tripBreadcrumbs = tripGroups[tripNumber];
                                      if (tripBreadcrumbs.length > 0) {
                                        const startTime = new Date(tripBreadcrumbs[0].location_time);
                                        const endTime = new Date(tripBreadcrumbs[tripBreadcrumbs.length - 1].location_time);
                                        const durationMinutes = Math.round((endTime - startTime) / (1000 * 60));
                                        const durationFormatted = formatMinutesToHours(durationMinutes);
                                        
                                        // Calculate actual trip distance from breadcrumbs
                                        let tripMiles = 0;
                                        for (let i = 1; i < tripBreadcrumbs.length; i++) {
                                          const prev = tripBreadcrumbs[i - 1];
                                          const curr = tripBreadcrumbs[i];
                                          if (prev.latitude && prev.longitude && curr.latitude && curr.longitude) {
                                            // Calculate distance using haversine formula
                                            const R = 3959; // Earth radius in miles
                                            const dLat = (curr.latitude - prev.latitude) * Math.PI / 180;
                                            const dLon = (curr.longitude - prev.longitude) * Math.PI / 180;
                                            const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                                                      Math.cos(prev.latitude * Math.PI / 180) * Math.cos(curr.latitude * Math.PI / 180) *
                                                      Math.sin(dLon / 2) * Math.sin(dLon / 2);
                                            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                                            tripMiles += R * c;
                                          }
                                        }
                                        
                                        // Build Google Maps links for trip start and end using breadcrumb coordinates if available
                                        const startLat = tripBreadcrumbs[0].latitude;
                                        const startLon = tripBreadcrumbs[0].longitude;
                                        const endLat = tripBreadcrumbs[tripBreadcrumbs.length - 1].latitude;
                                        const endLon = tripBreadcrumbs[tripBreadcrumbs.length - 1].longitude;

                                        const startHref = (startLat && startLon) ? `https://www.google.com/maps?q=${startLat},${startLon}` : null;
                                        const endHref = (endLat && endLon) ? `https://www.google.com/maps?q=${endLat},${endLon}` : null;

                                        details.push({
                                          label: `Trip ${tripNumber}`,
                                          value: (
                                            <span>
                                              {startHref ? (
                                                <a
                                                  href={startHref}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-blue-600 hover:text-blue-800 underline"
                                                >
                                                  {formatTime(startTime)}
                                                </a>
                                              ) : (
                                                <span>{formatTime(startTime)}</span>
                                              )}
                                              {' '}-
                                              {' '}
                                              {endHref ? (
                                                <a
                                                  href={endHref}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-blue-600 hover:text-blue-800 underline"
                                                >
                                                  {formatTime(endTime)}
                                                </a>
                                              ) : (
                                                <span>{formatTime(endTime)}</span>
                                              )}
                                              {` (${durationFormatted}) ${tripMiles.toFixed(1)}mi`}
                                            </span>
                                          ),
                                          colorClass: 'text-gray-600 text-sm'
                                        });
                                      }
                                    });
                                  }
                                  
                                  return details.length > 0 ? (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                      <div className="text-xs font-medium text-gray-500 mb-2">Morning Commute Details:</div>
                                      {details.map((detail, index) => (
                                        <div key={index} className={`flex justify-between items-start ${detail.spacingClass || ''}`}>
                                      {detail.label && detail.label.trim() !== '' && (
                                        <span className={detail.labelClass || 'text-sm text-gray-600'}>{detail.label}</span>
                                      )}
                                          <div className={`${detail.colorClass}`}>
                                            {detail.value}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                            );
                    })()}
                  </div>
                </div>

                {/* End of Day */}
                <div className="bg-white rounded-lg p-4 border border-indigo-200">
                  <h4 className="font-medium text-indigo-800 mb-3 flex items-center">
                    End of Day
                    {billing_analysis?.end_of_day_penalty_minutes > 0 && (
                      <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 rounded text-sm">
                        {formatMinutesToHours(billing_analysis.end_of_day_penalty_minutes)}
                      </span>
                    )}
                    {(() => {
                      // Calculate evening paid drive time
                      const eveningCommute = gps_tracking_data?.commute_data?.evening_commute;
                      const isDoorToDoorDay = gps_tracking_data?.commute_data?.is_door_to_door_day;
                      const azugaTime = eveningCommute?.minutes || 0;
                      const googleTime = eveningCommute?.expected_duration_minutes || 0;
                      const eveningDriveTime = Math.min(azugaTime, googleTime);
                      // For door-to-door days (Sundays/holidays), don't subtract 45 minutes
                      const eveningPaidDriveTime = isDoorToDoorDay ? eveningDriveTime : Math.max(0, eveningDriveTime - 45);
                      
                      return eveningPaidDriveTime > 0 && (
                        <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 rounded text-sm flex items-center">
                          🚗 {formatMinutesToHours(eveningPaidDriveTime)} paid drive
                        </span>
                      );
                    })()}

                  </h4>
                  <div className="space-y-3">
                    {(() => {
                      // Collect all end of day events with their timestamps
                      const endEvents = [];

                      // Find the last job with activity (arrival or completion)
                      let lastJobEndTime = null;
                      let lastJobCompleted = false;
                      let lastJobStDone = null;
                      let lastJobGpsDeparture = null;
                      
                      if (assignments && assignments.length > 0) {
                        // Start from the last assignment and work backwards
                        for (let i = assignments.length - 1; i >= 0; i--) {
                          const assignment = assignments[i];
                          
                          // Check if this assignment has arrival (gps_arrived_on or st_arrived_on)
                          const hasArrival = assignment.gps_arrived_on || assignment.st_arrived_on;
                          
                          // Check if this assignment has completion (st_done_on or gps_departure_on)
                          const hasCompletion = assignment.st_done_on || assignment.gps_departure_on;
                          
                          if (hasArrival || hasCompletion) {
                            // This is the last job with activity
                            if (hasCompletion) {
                              // Job was completed - prioritize Azuga GPS departure, fall back to ServiceTitan done
                              if (assignment.gps_departure_on) {
                                lastJobEndTime = new Date(assignment.gps_departure_on);
                                lastJobGpsDeparture = new Date(assignment.gps_departure_on);
                              } else if (assignment.st_done_on) {
                                lastJobEndTime = new Date(assignment.st_done_on);
                                lastJobStDone = new Date(assignment.st_done_on);
                              }
                              lastJobCompleted = true;
                            } else {
                              // Job was started but not completed - use arrival time as reference
                              if (assignment.gps_arrived_on) {
                                lastJobEndTime = new Date(assignment.gps_arrived_on);
                              } else if (assignment.st_arrived_on) {
                                lastJobEndTime = new Date(assignment.st_arrived_on);
                              }
                              lastJobCompleted = false;
                            }
                            break; // Found the last job with activity
                          }
                        }
                      }
                      
                      // Only use global fields if they match the last job activity
                      if (lastJobEndTime && gps_tracking_data.last_job_st_done) {
                        const globalStDone = new Date(gps_tracking_data.last_job_st_done);
                        // Check if global st_done is close to the last job end time (within 30 minutes)
                        if (Math.abs(globalStDone.getTime() - lastJobEndTime.getTime()) <= 30 * 60 * 1000) {
                          lastJobStDone = globalStDone;
                        }
                      }
                      
                      if (lastJobEndTime && gps_tracking_data.last_job_gps_departure) {
                        const globalGpsDeparture = new Date(gps_tracking_data.last_job_gps_departure);
                        // Check if global gps_departure is close to the last job end time (within 30 minutes)
                        if (Math.abs(globalGpsDeparture.getTime() - lastJobEndTime.getTime()) <= 30 * 60 * 1000) {
                          lastJobGpsDeparture = globalGpsDeparture;
                        }
                      }
                      
                      // Always prioritize global GPS data for lastJobEndTime
                      if (gps_tracking_data.last_job_gps_departure) {
                        lastJobEndTime = new Date(gps_tracking_data.last_job_gps_departure);
                        lastJobCompleted = true;
                        lastJobGpsDeparture = new Date(gps_tracking_data.last_job_gps_departure);
                      } else if (gps_tracking_data.last_job_st_done) {
                        lastJobEndTime = new Date(gps_tracking_data.last_job_st_done);
                        lastJobCompleted = true;
                        lastJobStDone = new Date(gps_tracking_data.last_job_st_done);
                      }
                      // Fallback to assignment data only if no global data available
                      else if (!lastJobEndTime) {
                        if (assignments && assignments.length > 0) {
                          // Use the assignment-based logic as fallback
                          for (let i = assignments.length - 1; i >= 0; i--) {
                            const assignment = assignments[i];
                            const hasArrival = assignment.gps_arrived_on || assignment.st_arrived_on;
                            const hasCompletion = assignment.st_done_on || assignment.gps_departure_on;
                            
                            if (hasArrival || hasCompletion) {
                              if (hasCompletion) {
                                if (assignment.gps_departure_on) {
                                  lastJobEndTime = new Date(assignment.gps_departure_on);
                                  lastJobGpsDeparture = new Date(assignment.gps_departure_on);
                                } else if (assignment.st_done_on) {
                                  lastJobEndTime = new Date(assignment.st_done_on);
                                  lastJobStDone = new Date(assignment.st_done_on);
                                }
                                lastJobCompleted = true;
                              } else {
                                if (assignment.gps_arrived_on) {
                                  lastJobEndTime = new Date(assignment.gps_arrived_on);
                                } else if (assignment.st_arrived_on) {
                                  lastJobEndTime = new Date(assignment.st_arrived_on);
                                }
                                lastJobCompleted = false;
                              }
                              break;
                            }
                          }
                        }
                      }

                      // Azuga Departure (Last Job) - always add from global data
                      if (gps_tracking_data.last_job_gps_departure) {
                        const globalGpsDeparture = new Date(gps_tracking_data.last_job_gps_departure);
                        endEvents.push({
                          time: globalGpsDeparture,
                          type: 'gps_departure',
                          label: 'Azuga Departure (Last Job)',
                          value: formatTime(globalGpsDeparture),
                          colorClass: 'text-blue-700',
                          timestamp: globalGpsDeparture.getTime()
                        });
                      }

                      // ServiceTitan Done (Last Job) - always add from global data
                      if (gps_tracking_data.last_job_st_done) {
                        const globalStDone = new Date(gps_tracking_data.last_job_st_done);
                        endEvents.push({
                          time: globalStDone,
                          type: 'st_done',
                          label: 'ServiceTitan Done (Last Job)',
                          value: formatEventTime(globalStDone, employee_info.work_date + 'T12:00:00'),
                          colorClass: 'text-gray-700',
                          timestamp: globalStDone.getTime()
                        });
                      }

                      // ADP Punch Out (last work punch)
                      // Handle both legacy format (array) and new format (object with adp_entries/tfda_shifts)
                      let timeEntries = [];
                      if (Array.isArray(adp_time_data.time_entries)) {
                        // Check if this is ADP data (has entryTotals) or TFDA data (has shift_number)
                        if (adp_time_data.time_entries.length > 0 && adp_time_data.time_entries[0].shift_number !== undefined) {
                          // This is TFDA data, not ADP - skip ADP processing
                          timeEntries = [];
                        } else {
                          // Legacy ADP format: direct array
                          timeEntries = adp_time_data.time_entries;
                        }
                      } else if (adp_time_data.time_entries && adp_time_data.time_entries.adp_entries) {
                        // New format: object with adp_entries and tfda_shifts
                        timeEntries = adp_time_data.time_entries.adp_entries || [];
                      }
                      const filteredTimeEntries = timeEntries.filter(entry => 
                        entry.entryTotals?.[0]?.payCode?.codeValue !== 'HOLIDAY'
                      );
                      const workEntries = filteredTimeEntries.filter(entry => 
                        entry.entryTotals?.[0]?.payCode?.codeValue === 'REGULAR' || 
                        entry.entryTotals?.[0]?.payCode?.codeValue === 'OVERTIME'
                      );
                      
                      const entryWithLatestPunchout = workEntries.reduce((latest, current) => {
                        if (!current.endPeriod?.endDateTime) return latest;
                        if (!latest.endPeriod?.endDateTime) return current;
                        return new Date(current.endPeriod.endDateTime) > new Date(latest.endPeriod.endDateTime) ? current : latest;
                      }, workEntries[0]);

                      if (entryWithLatestPunchout?.endPeriod?.endDateTime) {
                        const punchOutTime = new Date(entryWithLatestPunchout.endPeriod.endDateTime);
                        // Determine color based on timing relative to expected end
                        const expectedEndTime = billing_analysis?.end_of_day ? new Date(billing_analysis.end_of_day) : null;
                        let punchOutColor = 'text-red-700'; // default red
                        if (expectedEndTime) {
                          if (punchOutTime <= expectedEndTime) {
                            punchOutColor = 'text-green-700'; // green if before or equal to expected end
                          } else {
                            punchOutColor = 'text-red-700'; // red if after expected end
                          }
                        }
                        
                        endEvents.push({
                          time: punchOutTime,
                          type: 'punch_out',
                          label: 'ADP Punch Out',
                          value: formatEventTime(punchOutTime, employee_info.work_date + 'T12:00:00'),
                          colorClass: punchOutColor,
                          timestamp: punchOutTime.getTime(),
                          isAdpPunch: true
                        });
                      }

                      // TFDA Shift End
                      // Handle TFDA shifts from time_entries - support both formats
                      let tfdaShiftsEnd = [];
                      if (adp_time_data.time_entries) {
                        if (Array.isArray(adp_time_data.time_entries)) {
                          // TFDA-only format: direct array of shifts
                          // Check if this is TFDA data (has shift_number) vs ADP data (has entryTotals)
                          if (adp_time_data.time_entries.length > 0 && adp_time_data.time_entries[0].shift_number !== undefined) {
                            tfdaShiftsEnd = adp_time_data.time_entries;
                          }
                        } else if (adp_time_data.time_entries.tfda_shifts) {
                          // Merged format: object with tfda_shifts property
                          tfdaShiftsEnd = adp_time_data.time_entries.tfda_shifts;
                        }
                      }
                      
                      // Find latest TFDA shift end
                      if (tfdaShiftsEnd.length > 0) {
                        const latestShift = tfdaShiftsEnd.reduce((latest, current) => {
                          if (!current.shift_end) return latest;
                          if (!latest.shift_end) return current;
                          return new Date(current.shift_end) > new Date(latest.shift_end) ? current : latest;
                        }, tfdaShiftsEnd[0]);
                        
                        if (latestShift?.shift_end) {
                          const tfdaEndTime = new Date(latestShift.shift_end);
                          const expectedEndTime = billing_analysis?.end_of_day ? new Date(billing_analysis.end_of_day) : null;
                          
                          // Determine color based on timing relative to expected end
                          let tfdaEndColor = 'text-red-700'; // default red
                          if (expectedEndTime) {
                            if (tfdaEndTime <= expectedEndTime) {
                              tfdaEndColor = 'text-green-700'; // green if before or equal to expected end
                            } else {
                              tfdaEndColor = 'text-red-700'; // red if after expected end
                            }
                          }
                          
                          endEvents.push({
                            time: tfdaEndTime,
                            type: 'tfda_end',
                            label: 'TFDA Shift End',
                            value: formatEventTime(tfdaEndTime, employee_info.work_date + 'T12:00:00'),
                            colorClass: tfdaEndColor,
                            timestamp: tfdaEndTime.getTime(),
                            isTfdaShift: true
                          });
                        }
                      }

                      // Note: Office departures now handled in comprehensive visit data above


                      // Warehouse Departure (GPS)
                      if (gps_tracking_data.last_warehouse_departure) {
                        endEvents.push({
                          time: new Date(gps_tracking_data.last_warehouse_departure),
                          type: 'warehouse_departure',
                          label: 'Warehouse Departure',
                          value: formatTime(new Date(gps_tracking_data.last_warehouse_departure)),
                          colorClass: 'text-indigo-700',
                          timestamp: new Date(gps_tracking_data.last_warehouse_departure).getTime()
                        });
                      }

                      // Other Location Arrivals and Departures (end of day events)
                      if (gps_tracking_data.location_arrivals) {
                        try {
                          const locationData = typeof gps_tracking_data.location_arrivals === 'string' 
                            ? JSON.parse(gps_tracking_data.location_arrivals) 
                            : gps_tracking_data.location_arrivals;
                          
                          // Handle new comprehensive visit data structure - show all location types for end of day
                          if (locationData && locationData.all_visits && Array.isArray(locationData.all_visits)) {
                            locationData.all_visits.forEach(visit => {
                              // Add arrival event for all location types
                              if (visit.arrival_time) {
                                const arrivalTime = new Date(visit.arrival_time);
                                const locationTypeColor = visit.location_type === 'office' ? 'text-blue-700' : 
                                                         visit.location_type === 'warehouse' ? 'text-purple-700' :
                                                         visit.location_type === 'vendor' ? 'text-green-700' : 'text-teal-700';
                                endEvents.push({
                                  time: arrivalTime,
                                  type: `${visit.location_type}_location_arrival`,
                                  label: `${visit.location_name || 'Location'} Arrival`,
                                  value: formatTime(arrivalTime),
                                  colorClass: locationTypeColor,
                                  timestamp: arrivalTime.getTime(),
                                  duration: visit.duration_minutes,
                                  departureTime: visit.departure_time
                                });
                              }
                              // Add departure event (if not still inside)
                              if (visit.departure_time && !visit.still_inside) {
                                const departureTime = new Date(visit.departure_time);
                                const locationTypeColor = visit.location_type === 'office' ? 'text-orange-700' : 
                                                         visit.location_type === 'warehouse' ? 'text-red-700' :
                                                         visit.location_type === 'vendor' ? 'text-yellow-700' : 'text-amber-700';
                                endEvents.push({
                                  time: departureTime,
                                  type: `${visit.location_type}_location_departure`,
                                  label: `${visit.location_name || 'Location'} Departure`,
                                  value: formatTime(departureTime),
                                  colorClass: locationTypeColor,
                                  timestamp: departureTime.getTime(),
                                  duration: visit.duration_minutes,
                                  arrivalTime: visit.arrival_time
                                });
                              }
                            });
                          }
                          // Handle backward compatibility with first_arrivals_by_type
                          else if (locationData && locationData.first_arrivals_by_type) {
                            Object.entries(locationData.first_arrivals_by_type).forEach(([locationType, arrivalTime]) => {
                              if (locationType === 'other' && arrivalTime) {
                                const arrivalDateTime = new Date(arrivalTime);
                                endEvents.push({
                                  time: arrivalDateTime,
                                  type: 'other_location_arrival',
                                  label: `Other Location Arrival`,
                                  value: formatTime(arrivalDateTime),
                                  colorClass: 'text-teal-700',
                                  timestamp: arrivalDateTime.getTime()
                                });
                              }
                            });
                          }
                          // Handle legacy object format where keys are location types
                          else if (typeof locationData === 'object' && !Array.isArray(locationData)) {
                            Object.entries(locationData).forEach(([locationType, arrival]) => {
                              if (locationType === 'other' && arrival.arrival_time) {
                                const arrivalTime = new Date(arrival.arrival_time);
                                endEvents.push({
                                  time: arrivalTime,
                                  type: 'other_location_arrival',
                                  label: `${arrival.location_name || 'Other Location'} Arrival`,
                                  value: formatTime(arrivalTime),
                                  colorClass: 'text-teal-700',
                                  timestamp: arrivalTime.getTime()
                                });
                              }
                            });
                          }
                        } catch (error) {
                          console.error('Error parsing location_arrivals:', error);
                        }
                      }

                      // Note: location_departures processing removed - all departure data is now in location_arrivals.all_visits



                      // Filter events to only show those after the last job end time, but always include core end-of-day events
                      const filteredEndEvents = endEvents.filter(event => {
                        if (!lastJobEndTime) return true; // If no last job end time, show all events
                        if (event.type === 'punch_out' || event.type === 'st_done' || event.type === 'gps_departure' || event.type === 'tfda_end') return true; // Always show core end-of-day events
                        return event.timestamp >= lastJobEndTime.getTime();
                      });

                      console.log('Last job end time:', lastJobEndTime);
                      console.log('All end events before filtering:', endEvents.map(e => ({
                        type: e.type,
                        label: e.label,
                        value: e.value,
                        timestamp: e.timestamp,
                        time: e.time
                      })));
                      console.log('Filtered end events:', filteredEndEvents.map(e => ({
                        type: e.type,
                        label: e.label,
                        value: e.value,
                        timestamp: e.timestamp
                      })));

                      // Sort events by timestamp
                      filteredEndEvents.sort((a, b) => a.timestamp - b.timestamp);

                      // Highlight events that match the expected start/end of day times
                      const expectedStartTime = billing_analysis?.start_of_day ? new Date(billing_analysis.start_of_day) : null;
                      const expectedEndTime = billing_analysis?.end_of_day ? new Date(billing_analysis.end_of_day) : null;

                      // Add highlighting to events that match expected times exactly
                      filteredEndEvents.forEach(event => {
                        if (expectedStartTime && Math.abs(event.timestamp - expectedStartTime.getTime()) <= 30 * 1000) { // 30 seconds tolerance
                          event.isExpectedStart = true;
                        }
                        if (expectedEndTime && Math.abs(event.timestamp - expectedEndTime.getTime()) <= 30 * 1000) { // 30 seconds tolerance
                          event.isExpectedEnd = true;
                        }
                      });

                      console.log('Filtered and sorted end events:', filteredEndEvents.map(e => ({
                        type: e.type,
                        value: e.value,
                        timestamp: e.timestamp
                      })));

                      // Add events without timestamps at the end
                      const endEventsWithoutTime = [];

                      // Check for missing required events (only if they would be shown after filtering)
                      // Note: We no longer show "Not available" entries - just skip them
                      // if (!filteredEndEvents.some(e => e.type === 'st_done')) {
                      //   endEventsWithoutTime.push({
                      //     type: 'st_done',
                      //     label: 'ServiceTitan Done (Last Job)',
                      //     value: 'Not available',
                      //     colorClass: 'text-gray-700'
                      //   });
                      // }

                      // if (!filteredEndEvents.some(e => e.type === 'gps_departure')) {
                      //   endEventsWithoutTime.push({
                      //     type: 'gps_departure',
                      //     label: 'Azuga Departure (Last Job)',
                      //     value: 'Not available',
                      //     colorClass: 'text-blue-700'
                      //   });
                      // }

                      // if (!filteredEndEvents.some(e => e.type === 'punch_out')) {
                      //   endEventsWithoutTime.push({
                      //     type: 'punch_out',
                      //     label: 'ADP Punch Out',
                      //     value: 'Not available',
                      //     colorClass: 'text-red-700'
                      //   });
                      // }

                      return (
                        <div>
                          {[...filteredEndEvents, ...endEventsWithoutTime].map((event, index) => (
                            <div key={`${event.type}-${index}`} className={`flex justify-between items-center ${
                              event.isAdpPunch ? (event.colorClass === 'text-red-700' ? 'bg-red-50 border-l-4 border-red-500 pl-2' : 'bg-green-50 border-l-4 border-green-500 pl-2') :
                              event.isTfdaShift ? (event.colorClass === 'text-red-700' ? 'bg-red-50 border-l-4 border-red-500 pl-2' : 'bg-green-50 border-l-4 border-green-500 pl-2') :
                              event.isExpectedStart ? 'bg-blue-50 border-l-4 border-blue-600 pl-2' :
                              event.isExpectedEnd ? 'bg-blue-50 border-l-4 border-blue-600 pl-2' :
                              ''
                            }`}>
                              <span className="text-sm text-gray-600 flex items-center">
                                {event.label}
                              </span>
                              <span className={`font-medium ${event.colorClass}`}>
                                {event.value}
                              </span>
                            </div>
                          ))}

                          {/* Evening Commute Details */}
                          {gps_tracking_data.commute_data?.evening_commute && (() => {
                            // Check if end_of_day is an office arrival WITH NO DEPARTURE (if so, don't show evening commute)
                            const isEndOfDayOfficeArrivalWithNoDeparture = (() => {
                              if (!billing_analysis?.end_of_day || !gps_tracking_data.location_arrivals) {
                                return false;
                              }
                              
                              try {
                                const endOfDayTime = new Date(billing_analysis.end_of_day).getTime();
                                const locationData = typeof gps_tracking_data.location_arrivals === 'string' 
                                  ? JSON.parse(gps_tracking_data.location_arrivals) 
                                  : gps_tracking_data.location_arrivals;
                                
                                if (locationData && locationData.all_visits && Array.isArray(locationData.all_visits)) {
                                  return locationData.all_visits.some(visit => {
                                    if (visit.location_type === 'office' && visit.arrival_time) {
                                      const arrivalTime = new Date(visit.arrival_time).getTime();
                                      const timeDiff = Math.abs(endOfDayTime - arrivalTime);
                                      
                                      // Check if end_of_day matches this office arrival (within 5 minutes)
                                      if (timeDiff <= 300000) {
                                        // Only hide evening commute if there's NO departure time (they stayed at office)
                                        return !visit.departure_time || visit.departure_time === null;
                                      }
                                    }
                                    return false;
                                  });
                                }
                              } catch (error) {
                                console.error('Error checking end_of_day office arrival with no departure:', error);
                              }
                              return false;
                            })();
                            
                            // Don't show evening commute if end of day is an office arrival with no departure
                            if (isEndOfDayOfficeArrivalWithNoDeparture) {
                              return null;
                            }
                            const eveningCommute = gps_tracking_data.commute_data.evening_commute;
                            const details = [];
                            
                            // Commute time (start - end)
                            if (eveningCommute.start_time && eveningCommute.end_time) {
                              details.push({
                                label: 'Commute Time',
                                value: `${formatTime(new Date(eveningCommute.start_time))} - ${formatTime(new Date(eveningCommute.end_time))}`,
                                colorClass: 'text-purple-700 font-bold'
                              });
                            }
                            
                            // Address flow (start → end)
                            if (eveningCommute.start_address && eveningCommute.end_address) {
                              details.push({
                                label: '',
                                value: (
                                  <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-purple-200">
                                    <div className="flex items-center text-sm text-gray-700 mb-2">
                                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                      <div className="truncate">
                                        {(() => {
                                          const breadcrumbs = eveningCommute.breadcrumbs || [];
                                          const startLat = eveningCommute.start_latitude ?? (breadcrumbs[0]?.latitude);
                                          const startLon = eveningCommute.start_longitude ?? (breadcrumbs[0]?.longitude);
                                          const startHref = (startLat != null && startLon != null)
                                            ? `https://www.google.com/maps?q=${startLat},${startLon}`
                                            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(eveningCommute.start_address)}`;
                                          return (
                                            <a
                                              href={startHref}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 underline"
                                            >
                                              {eveningCommute.start_address}
                                            </a>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                    <div className="flex items-center text-sm text-gray-700">
                                      <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                                      <div className="truncate">
                                        {(() => {
                                          const breadcrumbs = eveningCommute.breadcrumbs || [];
                                          const endLat = eveningCommute.end_latitude ?? (breadcrumbs[breadcrumbs.length - 1]?.latitude);
                                          const endLon = eveningCommute.end_longitude ?? (breadcrumbs[breadcrumbs.length - 1]?.longitude);
                                          const endHref = (endLat != null && endLon != null)
                                            ? `https://www.google.com/maps?q=${endLat},${endLon}`
                                            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(eveningCommute.end_address)}`;
                                          return (
                                            <a
                                              href={endHref}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 underline"
                                            >
                                              {eveningCommute.end_address}
                                            </a>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  </div>
                                ),
                                colorClass: 'w-full',
                                spacingClass: 'mt-2'
                              });
                            }
                            
                            // Determine which time is lower for styling
                            const eveningAzugaTime = eveningCommute?.minutes || 0;
                            const eveningGoogleTime = eveningCommute?.expected_duration_minutes || 0;
                            const isEveningGoogleLower = eveningGoogleTime > 0 && eveningAzugaTime > 0 ? eveningGoogleTime <= eveningAzugaTime : eveningGoogleTime > 0;
                            
                            // Google Route duration (styled based on whether it's being used)
                            if (eveningCommute.expected_duration_minutes) {
                              details.push({
                                label: 'Commute Duration',
                                value: (
                                  <span className="flex items-center">
                                    <span className="text-xs text-gray-500 mr-1">Google Route</span>
                                    <Route className="h-4 w-4 mr-1" />
                                    {formatMinutesToHours(eveningCommute.expected_duration_minutes)}
                                  </span>
                                ),
                                colorClass: isEveningGoogleLower ? 'text-green-700 font-bold' : 'text-gray-600 text-sm italic',
                                spacingClass: 'mt-3'
                              });
                            }
                            
                            // Azuga duration (styled based on whether it's being used)
                            if (eveningCommute.minutes) {
                              details.push({
                                label: '',
                                value: (
                                  <span className="flex items-center">
                                    <span className="text-xs text-gray-500 mr-1">Azuga</span>
                                    <Truck className="h-4 w-4 mr-1" />
                                    {formatMinutesToHours(eveningCommute.minutes)}
                                  </span>
                                ),
                                colorClass: !isEveningGoogleLower ? 'text-green-700 font-bold' : 'text-gray-600 text-sm italic',
                                spacingClass: 'mb-2'
                              });
                            }
                            
                            // Google Route distance (styled based on duration comparison)
                            if (eveningCommute.expected_distance_miles) {
                              details.push({
                                label: 'Commute Distance',
                                value: (
                                  <span className="flex items-center">
                                    <span className="text-xs text-gray-500 mr-1">Google Route</span>
                                    <Route className="h-4 w-4 mr-1" />
                                    {eveningCommute.expected_distance_miles.toFixed(2)} miles
                                  </span>
                                ),
                                colorClass: isEveningGoogleLower ? 'text-blue-700 font-bold' : 'text-gray-600 text-sm italic'
                              });
                            }
                            
                            // Azuga distance (styled based on duration comparison)
                            if (eveningCommute.miles) {
                              details.push({
                                label: '',
                                value: (
                                  <span className="flex items-center">
                                    <span className="text-xs text-gray-500 mr-1">Azuga</span>
                                    <Truck className="h-4 w-4 mr-1" />
                                    {eveningCommute.miles.toFixed(2)} miles
                                  </span>
                                ),
                                colorClass: !isEveningGoogleLower ? 'text-blue-700 font-bold' : 'text-gray-600 text-sm italic'
                              });
                            }
                            
                            // Individual trip details
                            if (eveningCommute.breadcrumbs && eveningCommute.breadcrumbs.length > 0) {
                              // Add spacing before trips
                              details.push({
                                label: '',
                                value: '',
                                colorClass: 'mt-2'
                              });
                              
                              // Group breadcrumbs by trip_number
                              const tripGroups = {};
                              eveningCommute.breadcrumbs.forEach(breadcrumb => {
                                const tripNum = breadcrumb.trip_number;
                                if (tripNum) {
                                  if (!tripGroups[tripNum]) {
                                    tripGroups[tripNum] = [];
                                  }
                                  tripGroups[tripNum].push(breadcrumb);
                                }
                              });
                              
                              // Add trip details for each trip
                              Object.keys(tripGroups).forEach((tripNumber, index) => {
                                const tripBreadcrumbs = tripGroups[tripNumber];
                                if (tripBreadcrumbs.length > 0) {
                                  const startTime = new Date(tripBreadcrumbs[0].location_time);
                                  const endTime = new Date(tripBreadcrumbs[tripBreadcrumbs.length - 1].location_time);
                                  const durationMinutes = Math.round((endTime - startTime) / (1000 * 60));
                                  const durationFormatted = formatMinutesToHours(durationMinutes);
                                  
                                  // Calculate actual trip distance from breadcrumbs
                                  let tripMiles = 0;
                                  for (let i = 1; i < tripBreadcrumbs.length; i++) {
                                    const prev = tripBreadcrumbs[i - 1];
                                    const curr = tripBreadcrumbs[i];
                                    if (prev.latitude && prev.longitude && curr.latitude && curr.longitude) {
                                      // Calculate distance using haversine formula
                                      const R = 3959; // Earth radius in miles
                                      const dLat = (curr.latitude - prev.latitude) * Math.PI / 180;
                                      const dLon = (curr.longitude - prev.longitude) * Math.PI / 180;
                                      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                                                Math.cos(prev.latitude * Math.PI / 180) * Math.cos(curr.latitude * Math.PI / 180) *
                                                Math.sin(dLon / 2) * Math.sin(dLon / 2);
                                      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                                      tripMiles += R * c;
                                    }
                                  }
                                  
                                  // Build Google Maps links for trip start and end using breadcrumb coordinates if available
                                  const startLat = tripBreadcrumbs[0].latitude;
                                  const startLon = tripBreadcrumbs[0].longitude;
                                  const endLat = tripBreadcrumbs[tripBreadcrumbs.length - 1].latitude;
                                  const endLon = tripBreadcrumbs[tripBreadcrumbs.length - 1].longitude;

                                  const startHref = (startLat && startLon) ? `https://www.google.com/maps?q=${startLat},${startLon}` : null;
                                  const endHref = (endLat && endLon) ? `https://www.google.com/maps?q=${endLat},${endLon}` : null;

                                  details.push({
                                    label: `Trip ${tripNumber}`,
                                    value: (
                                      <span>
                                        {startHref ? (
                                          <a
                                            href={startHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 underline"
                                          >
                                            {formatTime(startTime)}
                                          </a>
                                        ) : (
                                          <span>{formatTime(startTime)}</span>
                                        )}
                                        {' '}-
                                        {' '}
                                        {endHref ? (
                                          <a
                                            href={endHref}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 underline"
                                          >
                                            {formatTime(endTime)}
                                          </a>
                                        ) : (
                                          <span>{formatTime(endTime)}</span>
                                        )}
                                        {` (${durationFormatted}) ${tripMiles.toFixed(1)}mi`}
                                      </span>
                                    ),
                                    colorClass: 'text-gray-600 text-sm'
                                  });
                                }
                              });
                            }
                            
                            // Add ignored trips (greyed out)
                            if (eveningCommute.ignored_trips && eveningCommute.ignored_trips.length > 0) {
                              // Add separator
                              details.push({
                                label: '',
                                value: '',
                                colorClass: ''
                              });
                              
                              eveningCommute.ignored_trips.forEach((ignoredTrip, index) => {
                                const startTime = new Date(ignoredTrip.start_time);
                                const endTime = new Date(ignoredTrip.end_time);
                                const durationMinutes = Math.round((endTime - startTime) / (1000 * 60));
                                const durationFormatted = formatMinutesToHours(durationMinutes);

                                let reasonText = '';
                                if (ignoredTrip.gap_minutes !== null) {
                                  reasonText = ` (${formatMinutesToHours(ignoredTrip.gap_minutes)} stop)`;
                                }

                                const startHref = (ignoredTrip.start_latitude != null && ignoredTrip.start_longitude != null)
                                  ? `https://www.google.com/maps/search/?api=1&query=${ignoredTrip.start_latitude},${ignoredTrip.start_longitude}`
                                  : (ignoredTrip.start_address
                                    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ignoredTrip.start_address)}`
                                    : null);
                                const endHref = (ignoredTrip.end_latitude != null && ignoredTrip.end_longitude != null)
                                  ? `https://www.google.com/maps/search/?api=1&query=${ignoredTrip.end_latitude},${ignoredTrip.end_longitude}`
                                  : (ignoredTrip.end_address
                                    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ignoredTrip.end_address)}`
                                    : null);

                                details.push({
                                  label: `Trip ${ignoredTrip.trip_number}`,
                                  value: (
                                    <span>
                                      {startHref ? (
                                        <a
                                          href={startHref}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800 underline"
                                        >
                                          {formatTime(startTime)}
                                        </a>
                                      ) : (
                                        <span>{formatTime(startTime)}</span>
                                      )}
                                      {' '}-
                                      {' '}
                                      {endHref ? (
                                        <a
                                          href={endHref}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800 underline"
                                        >
                                          {formatTime(endTime)}
                                        </a>
                                      ) : (
                                        <span>{formatTime(endTime)}</span>
                                      )}
                                      {` (${durationFormatted})${reasonText}`}
                                    </span>
                                  ),
                                  colorClass: 'text-gray-400 text-sm opacity-60',
                                  labelClass: 'text-gray-400 text-sm opacity-60'
                                });
                              });
                            }
                            
                            return details.length > 0 ? (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="text-xs font-medium text-gray-500 mb-2">Evening Commute Details:</div>
                                {details.map((detail, index) => (
                                  <div key={index} className={`flex justify-between items-start ${detail.spacingClass || ''}`}>
                                    {detail.label && detail.label.trim() !== '' && (
                                      <span className="text-sm text-gray-600">{detail.label}</span>
                                    )}
                                    <div className={`${detail.colorClass}`}>
                                      {detail.value}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* ADP Time Entries */}
            {(() => {
              // Check if we have ADP data to show
              // Only look at time_entries to determine if there's ADP data
              // (punch times are populated by both ADP and TFDA)
              let hasAdpData = false;
              
              // Check for ADP time entries only
              if (Array.isArray(adp_time_data.time_entries)) {
                // If it's an array, check if it contains ADP data (not TFDA data)
                if (adp_time_data.time_entries.length > 0 && 
                    adp_time_data.time_entries[0].shift_number === undefined) {
                  hasAdpData = true;
                }
              } else if (adp_time_data.time_entries && adp_time_data.time_entries.adp_entries) {
                // New structured format - check if ADP entries exist
                if (adp_time_data.time_entries.adp_entries.length > 0) {
                  hasAdpData = true;
                }
              }
              
              if (!hasAdpData) {
                return null; // Don't show ADP section if no ADP data
              }
              
              return (
                <div className={`rounded-lg p-4 ${(!adp_time_data.first_punch_in && !adp_time_data.last_punch_out) ? 'bg-yellow-50 border border-yellow-300' : 'bg-blue-50'}`}>
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <Clock className={`h-5 w-5 mr-2 ${(!adp_time_data.first_punch_in && !adp_time_data.last_punch_out) ? 'text-yellow-600' : 'text-blue-500'}`} />
                ADP Time Entries
              </h3>
              {(!adp_time_data.first_punch_in && !adp_time_data.last_punch_out) && (
                <div className="mb-3 bg-yellow-100 border border-yellow-300 rounded px-2 py-1 flex items-center">
                  <AlertTriangle className="h-3 w-3 text-yellow-700 mr-1.5 flex-shrink-0" />
                  <span className="text-xs text-yellow-800 font-medium">
                    No ADP punch-in/out recorded on this day
                  </span>
                </div>
              )}
              
              {/* No Lunch Break Detection */}
              {(() => {
                const noLunchDiscrepancies = filteredDiscrepancies.filter(disc => 
                  disc.discrepancy_type === 'no_lunch_break'
                );
                
                if (noLunchDiscrepancies.length > 0) {
                  const discrepancy = noLunchDiscrepancies[0];
                  
                  return (
                    <div className="mb-3 bg-red-100 border border-red-300 rounded px-2 py-1 flex items-center">
                      <AlertTriangle className="h-3 w-3 text-red-600 mr-1.5 flex-shrink-0" />
                      <span className="text-xs text-red-800 font-medium">
                        Employee worked {formatMinutesToHours(discrepancy.actual_value)} without taking a lunch break
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Time Entries Details */}
              {(() => {
                // Handle both legacy format (array) and new format (object with adp_entries/tfda_shifts)
                let timeEntries = [];
                if (Array.isArray(adp_time_data.time_entries)) {
                  timeEntries = adp_time_data.time_entries;
                } else if (adp_time_data.time_entries && adp_time_data.time_entries.adp_entries) {
                  timeEntries = adp_time_data.time_entries.adp_entries || [];
                }
                return timeEntries && timeEntries.length > 0;
              })() && (
                <div>
                  <div className="space-y-2">
                    {(() => {
                      // Get the correct time entries array
                      let timeEntries = [];
                      if (Array.isArray(adp_time_data.time_entries)) {
                        // Check if this is ADP data (has entryTotals) or TFDA data (has shift_number)
                        if (adp_time_data.time_entries.length > 0 && adp_time_data.time_entries[0].shift_number !== undefined) {
                          // This is TFDA data, not ADP - skip ADP processing
                          timeEntries = [];
                        } else {
                          // Legacy ADP format: direct array
                          timeEntries = adp_time_data.time_entries;
                        }
                      } else if (adp_time_data.time_entries && adp_time_data.time_entries.adp_entries) {
                        timeEntries = adp_time_data.time_entries.adp_entries || [];
                      }
                      return timeEntries;
                    })()
                      .sort((a, b) => {
                        const aStart = a.startPeriod?.startDateTime ? new Date(a.startPeriod.startDateTime) : new Date(0);
                        const bStart = b.startPeriod?.startDateTime ? new Date(b.startPeriod.startDateTime) : new Date(0);
                        return aStart - bStart;
                      })
                      .map((entry, index) => {
                        const startDateTime = entry.startPeriod?.startDateTime ? new Date(entry.startPeriod.startDateTime) : null;
                        const endDateTime = entry.endPeriod?.endDateTime ? new Date(entry.endPeriod.endDateTime) : null;
                        
                        // Check if the time spans different days
                        const sameDay = startDateTime && endDateTime && 
                          startDateTime.toDateString() === endDateTime.toDateString();
                        
                        // Check for validation issues
                        const hasError = entry.entryStatusCode?.codeValue === 'Error';
                        
                        // Check for meaningful exceptions (excluding overtime-only warnings)
                        const meaningfulExceptions = entry.exceptions?.filter(exception => 
                          exception.exceptionDescription !== 'Overtime hours found'
                        ) || [];
                        
                        const hasWarning = entry.entryStatusCode?.codeValue === 'Warning' && meaningfulExceptions.length > 0;
                        const hasExceptions = meaningfulExceptions.length > 0;
                        
                        // Determine border and background color based on validation status
                        let borderColor = 'border-gray-200';
                        let bgColor = 'bg-white';
                        
                        if (hasError) {
                          borderColor = 'border-red-300';
                          bgColor = 'bg-red-50';
                        } else if (hasWarning) {
                          borderColor = 'border-yellow-300';
                          bgColor = 'bg-yellow-50';
                        }
                        
                        return (
                          <div key={index} className={`${bgColor} p-3 rounded border ${borderColor} text-sm`}>
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <p className="font-medium">
                                    {startDateTime 
                                      ? (sameDay 
                                          ? startDateTime.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
                                          : startDateTime.toLocaleDateString() + ' ' + startDateTime.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
                                        )
                                      : 'N/A'} - {endDateTime 
                                      ? (sameDay 
                                          ? endDateTime.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
                                          : endDateTime.toLocaleDateString() + ' ' + endDateTime.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
                                        )
                                      : 'N/A'}
                                  </p>
                                </div>
                                <p className="text-gray-600">
                                  Duration: {entry.timeDuration || 'N/A'}
                                </p>
                              </div>
                              <div className="text-right flex flex-col items-end space-y-1">
                                
                                
                                {/* Pay code badge */}
                                {entry.entryTotals && entry.entryTotals[0] && (
                                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                                    {entry.entryTotals[0].payCode?.codeValue || 'REGULAR'}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Show validation exceptions - full width */}
                            {hasExceptions && (
                              <div className="mt-2 space-y-1">
                                {meaningfulExceptions.map((exception, excIndex) => (
                                  <div key={excIndex} className={`text-xs px-2 py-1 rounded ${
                                    exception.exceptionTypeCode?.codeValue === 'error' 
                                      ? 'bg-red-100 text-red-700' 
                                      : 'bg-yellow-50 text-yellow-800'
                                  }`}>
                                    <span className="font-medium">
                                      {exception.exceptionTypeCode?.codeValue === 'error' ? '❌' : '⚠️'} 
                                    </span>
                                    {exception.exceptionDescription}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
                </div>
              );
            })()}

            {/* TFDA Shifts */}
            {(() => {
              let tfdaShifts = [];
              
              if (!adp_time_data.time_entries) {
                return null;
              }
              
              if (Array.isArray(adp_time_data.time_entries)) {
                // Check if this array contains TFDA shift objects (has shift_number property)
                if (adp_time_data.time_entries.length > 0 && adp_time_data.time_entries[0].shift_number !== undefined) {
                  tfdaShifts = adp_time_data.time_entries;
                } else {
                  return null;
                }
              } else if (adp_time_data.time_entries.tfda_shifts) {
                // New structured format
                tfdaShifts = adp_time_data.time_entries.tfda_shifts || [];
              } else {
                return null;
              }
              
              if (tfdaShifts.length === 0) {
                return null;
              }
              
              return (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-blue-500" />
                    TFDA Shifts
                  </h3>
                  <div className="space-y-3">
                    {tfdaShifts.map((shift, index) => (
                      <div key={index} className="bg-white rounded-lg border border-gray-200 p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                              Shift {shift.shift_number}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              {formatMinutesToHours(shift.regular_time_in_minutes)}
                            </span>
                          </div>
                          <div className="text-right font-bold text-sm text-gray-900">
                            {shift.shift_start && shift.shift_end && (
                              <>
                                {formatTime(new Date(shift.shift_start))} - {formatTime(new Date(shift.shift_end))}
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Jobs in this shift */}
                        {shift.jobs && shift.jobs.length > 0 && (
                          <div className="mb-2">
                            <div className="space-y-1">
                              {shift.jobs.map((job, jobIndex) => {
                                // Find corresponding assignment data for this job
                                // Strip suffix from TFDA job number (e.g., "3044847-0000" -> "3044847")
                                const baseJobNumber = job.job_number && job.job_number.includes('-') 
                                  ? job.job_number.split('-')[0] 
                                  : job.job_number;
                                const correspondingAssignment = assignments.find(assignment => 
                                  assignment.job_number === baseJobNumber
                                );
                                
                                // Validate TFDA start time against actual arrivals
                                let tfdaStartValidation = null;
                                if (job.start && correspondingAssignment) {
                                  const tfdaStart = new Date(job.start);
                                  const azugaArrival = correspondingAssignment.gps_arrived_on ? new Date(correspondingAssignment.gps_arrived_on) : null;
                                  const stArrival = correspondingAssignment.st_arrived_on ? new Date(correspondingAssignment.st_arrived_on) : null;
                                  
                                  // Use Azuga arrival if available, otherwise ServiceTitan arrival
                                  const referenceArrival = azugaArrival || stArrival;
                                  const referenceSource = azugaArrival ? 'Azuga' : 'ServiceTitan';
                                  
                                  if (referenceArrival) {
                                    const timeDiffMinutes = (tfdaStart - referenceArrival) / (1000 * 60);
                                    
                                if (timeDiffMinutes < 0) {
                                  // TFDA start is before actual arrival - BAD (any amount)
                                  tfdaStartValidation = {
                                    status: 'early',
                                    message: `TFDA start ${Math.abs(Math.round(timeDiffMinutes))}min before ${referenceSource} arrival`,
                                    severity: 'warning'
                                  };
                                } else if (timeDiffMinutes >= 0 && timeDiffMinutes <= 60) {
                                  // TFDA start is after arrival (positive) - GOOD
                                  tfdaStartValidation = {
                                    status: 'valid',
                                    message: `TFDA start aligns with ${referenceSource} arrival`,
                                    severity: 'success'
                                  };
                                } else {
                                  // TFDA start is way too late (more than 60 minutes after arrival) - BAD
                                  tfdaStartValidation = {
                                    status: 'late',
                                    message: `TFDA start ${Math.round(timeDiffMinutes)}min after ${referenceSource} arrival`,
                                    severity: 'warning'
                                  };
                                }
                                  }
                                }

                                // Validate TFDA end time against actual departures
                                let tfdaEndValidation = null;
                                if (job.end && correspondingAssignment) {
                                  const tfdaEnd = new Date(job.end);
                                  const azugaDeparture = correspondingAssignment.gps_departure_on ? new Date(correspondingAssignment.gps_departure_on) : null;
                                  const stDone = correspondingAssignment.st_done_on ? new Date(correspondingAssignment.st_done_on) : null;
                                  
                                  // Use Azuga departure if available, otherwise ServiceTitan done
                                  const referenceDeparture = azugaDeparture || stDone;
                                  const referenceSource = azugaDeparture ? 'Azuga' : 'ServiceTitan';
                                  
                                  if (referenceDeparture) {
                                    const timeDiffMinutes = (tfdaEnd - referenceDeparture) / (1000 * 60);
                                    
                                if (timeDiffMinutes > 0) {
                                  // TFDA end is after actual departure - BAD (any amount)
                                  tfdaEndValidation = {
                                    status: 'late',
                                    message: `TFDA end ${Math.round(timeDiffMinutes)}min after ${referenceSource} departure`,
                                    severity: 'warning'
                                  };
                                } else {
                                  // TFDA end is before departure (negative) - GOOD (any amount)
                                  tfdaEndValidation = {
                                    status: 'valid',
                                    message: `TFDA end aligns with ${referenceSource} departure`,
                                    severity: 'success'
                                  };
                                }
                                  }
                                }

                                // Combine validations for display
                                let combinedValidation = null;
                                const hasStartIssue = tfdaStartValidation && tfdaStartValidation.severity !== 'success';
                                const hasEndIssue = tfdaEndValidation && tfdaEndValidation.severity !== 'success';
                                
                                if (hasStartIssue && hasEndIssue) {
                                  // Both start and end have issues - create combined message
                                  const startDiff = Math.abs(Math.round((new Date(job.start) - (correspondingAssignment.gps_arrived_on ? new Date(correspondingAssignment.gps_arrived_on) : new Date(correspondingAssignment.st_arrived_on))) / (1000 * 60)));
                                  const endDiff = Math.abs(Math.round((new Date(job.end) - (correspondingAssignment.gps_departure_on ? new Date(correspondingAssignment.gps_departure_on) : new Date(correspondingAssignment.st_done_on))) / (1000 * 60)));
                                  const startRef = correspondingAssignment.gps_arrived_on ? 'Azuga' : 'ServiceTitan';
                                  const endRef = correspondingAssignment.gps_departure_on ? 'Azuga' : 'ServiceTitan';
                                  
                                  const worstSeverity = (tfdaStartValidation.severity === 'warning' || tfdaEndValidation.severity === 'warning') ? 'warning' : 'info';
                                  
                                  combinedValidation = {
                                    status: 'multiple',
                                    message: `TFDA timing issues: start ${startDiff}min off ${startRef}, end ${endDiff}min off ${endRef}`,
                                    severity: worstSeverity
                                  };
                                } else if (hasStartIssue) {
                                  combinedValidation = tfdaStartValidation;
                                } else if (hasEndIssue) {
                                  combinedValidation = tfdaEndValidation;
                                }
                                
                                return (
                                  <div key={jobIndex} className="bg-white p-3 rounded border">
                                    {/* Job Header */}
                                    <div className="flex items-center space-x-3 mb-2">
                                      <div className="flex items-center">
                                        <span>Job #{job.job_number}</span>
                                      </div>
                                  {job.paycode && (
                                        <div className="text-sm text-gray-500">
                                      {job.paycode}
                                        </div>
                                  )}
                                      <div className="text-sm text-gray-900">
                                        {new Date(Math.floor(new Date(job.start).getTime() / 60000) * 60000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {new Date(Math.floor(new Date(job.end).getTime() / 60000) * 60000).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                      </div>
                                    </div>
                                    
                                    {/* Timing Section - Match Job Assignment Layout */}
                                    <div className="grid grid-cols-2 gap-6 text-sm mb-2">
                                      {/* Left Column: TFDA Start */}
                                      <div className="space-y-2">
                                        {tfdaStartValidation && (
                                          <div className={`text-xs px-2 py-1 rounded ${
                                            tfdaStartValidation.severity === 'warning' 
                                              ? 'bg-red-50 text-red-700 border border-red-500' 
                                              : tfdaStartValidation.severity === 'success'
                                              ? 'bg-green-50 text-green-700 border border-green-500'
                                              : 'bg-blue-50 text-blue-700 border border-blue-500'
                                          }`}>
                                            {(() => {
                                              const tfdaStart = new Date(job.start);
                                              const azugaArrival = correspondingAssignment?.gps_arrived_on ? new Date(correspondingAssignment.gps_arrived_on) : null;
                                              const stArrival = correspondingAssignment?.st_arrived_on ? new Date(correspondingAssignment.st_arrived_on) : null;
                                              const referenceArrival = azugaArrival || stArrival;
                                              const referenceSource = azugaArrival ? 'Azuga' : 'ServiceTitan';
                                              
                                              // Round TFDA time down, reference time to nearest minute before calculating difference
                                              const tfdaStartRounded = new Date(Math.floor(tfdaStart.getTime() / 60000) * 60000);
                                              const referenceArrivalRounded = new Date(Math.round(referenceArrival.getTime() / 60000) * 60000);
                                              const timeDiffMinutes = Math.round((tfdaStartRounded - referenceArrivalRounded) / (1000 * 60));
                                              
                                              const absMinutes = Math.abs(timeDiffMinutes);
                                              const direction = timeDiffMinutes >= 0 ? 'after' : 'before';
                                              const sourceLabel = referenceSource === 'Azuga' ? 'Azuga' : 'ST';
                                              const actionLabel = referenceSource === 'Azuga' ? 'arrival' : 'arrived';
                                              return absMinutes === 0 
                                                ? `TFDA start at ${sourceLabel} ${actionLabel}` 
                                                : `TFDA start ${formatMinutesToHours(absMinutes)} ${direction} ${sourceLabel} ${actionLabel}`;
                                            })()}
                                          </div>
                                        )}
                                        
                                        {/* ServiceTitan Arrival */}
                                        {correspondingAssignment?.st_arrived_on && (
                                          <div className="flex items-center justify-between text-gray-600">
                                            <span>ServiceTitan:</span>
                                            <div className="text-right">
                                              <div>{new Date(correspondingAssignment.st_arrived_on).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</div>
                                              {correspondingAssignment.st_arrived_on_feet_from_job_site && (
                                                <div className="text-xs text-gray-500">
                                                  ({(correspondingAssignment.st_arrived_on_feet_from_job_site / 5280).toFixed(1)}mi)
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Azuga Arrival */}
                                        {correspondingAssignment?.gps_arrived_on && (
                                          <div className="flex items-center justify-between text-gray-600">
                                            <span>Azuga:</span>
                                            <span>{new Date(correspondingAssignment.gps_arrived_on).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Right Column: End Validation & Departures */}
                                      <div className="space-y-2">
                                        {tfdaEndValidation && (
                                          <div className={`text-xs px-2 py-1 rounded ${
                                            tfdaEndValidation.severity === 'warning' 
                                              ? 'bg-red-50 text-red-700 border border-red-500' 
                                              : tfdaEndValidation.severity === 'success'
                                              ? 'bg-green-50 text-green-700 border border-green-500'
                                              : 'bg-blue-50 text-blue-700 border border-blue-500'
                                          }`}>
                                            {(() => {
                                              const tfdaEnd = new Date(job.end);
                                              const azugaDeparture = correspondingAssignment?.gps_departure_on ? new Date(correspondingAssignment.gps_departure_on) : null;
                                              const stDone = correspondingAssignment?.st_done_on ? new Date(correspondingAssignment.st_done_on) : null;
                                              const referenceDeparture = azugaDeparture || stDone;
                                              const referenceSource = azugaDeparture ? 'Azuga' : 'ServiceTitan';
                                              
                                              // Round TFDA time down, reference time to nearest minute before calculating difference
                                              const tfdaEndRounded = new Date(Math.floor(tfdaEnd.getTime() / 60000) * 60000);
                                              const referenceDepartureRounded = new Date(Math.round(referenceDeparture.getTime() / 60000) * 60000);
                                              const timeDiffMinutes = Math.round((tfdaEndRounded - referenceDepartureRounded) / (1000 * 60));
                                              
                                              const absMinutes = Math.abs(timeDiffMinutes);
                                              const direction = timeDiffMinutes >= 0 ? 'after' : 'before';
                                              const sourceLabel = referenceSource === 'Azuga' ? 'Azuga' : 'ST';
                                              const actionLabel = referenceSource === 'Azuga' ? 'departure' : 'done';
                                              return absMinutes === 0 
                                                ? `TFDA end at ${sourceLabel} ${actionLabel}` 
                                                : `TFDA end ${formatMinutesToHours(absMinutes)} ${direction} ${sourceLabel} ${actionLabel}`;
                                            })()}
                                          </div>
                                        )}
                                        
                                        {/* ServiceTitan Done */}
                                        {correspondingAssignment?.st_done_on && (
                                          <div className="flex items-center justify-between text-gray-600">
                                            <span>ServiceTitan:</span>
                                            <div className="text-right">
                                              <div>
                                                {(() => {
                                                  const doneTime = new Date(correspondingAssignment.st_done_on);
                                                  const workDate = new Date(employee_info.work_date + 'T12:00:00');
                                                  const isDifferentDay = doneTime.toDateString() !== workDate.toDateString();
                                                  
                                                  const timeString = doneTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                                                  
                                                  if (isDifferentDay) {
                                                    const dateString = doneTime.toLocaleDateString([], { month: 'numeric', day: 'numeric' });
                                                    return `${timeString} (${dateString})`;
                                                  }
                                                  
                                                  return timeString;
                                                })()}
                                              </div>
                                              {correspondingAssignment.st_done_on_feet_from_job_site && (
                                                <div className="text-xs text-gray-500">
                                                  ({(correspondingAssignment.st_done_on_feet_from_job_site / 5280).toFixed(1)}mi)
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Azuga Departure */}
                                        {correspondingAssignment?.gps_departure_on && (
                                          <div className="flex items-center justify-between text-gray-600">
                                            <span>Azuga:</span>
                                            <span>{new Date(correspondingAssignment.gps_departure_on).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Duration */}
                                    <div className="text-sm text-gray-500 border-t pt-2">
                                      <span>Duration: </span>
                                      <span className="font-medium">
                                        {formatMinutesToHours(Math.round((new Date(job.end) - new Date(job.start)) / (1000 * 60)))}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Lunches in this shift */}
                        {(() => {
                          // Filter out lunches with null start and end times
                          const validLunches = shift.lunches?.filter(lunch => lunch.start && lunch.end) || [];
                          return validLunches.length > 0 && (
                            <div>
                              <div className="text-xs font-medium text-gray-700 mb-1">Lunches:</div>
                              <div className="space-y-1">
                                {validLunches.map((lunch, lunchIndex) => (
                                  <div key={lunchIndex} className="text-xs bg-orange-50 rounded px-2 py-1">
                                    <span className="text-gray-600">
                                      {new Date(lunch.start).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - 
                                      {new Date(lunch.end).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Job Assignments */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <Navigation className="h-5 w-5 mr-2 text-blue-500" />
                  Job Assignments
                </h3>

              </div>
              {assignments && assignments.length > 0 ? (
                <div className="space-y-3">
                  {assignments.sort((a, b) => {
                    // Sort by arrival time - assignments with arrival times first, ordered chronologically
                    const aArrival = a.gps_arrived_on || a.st_arrived_on;
                    const bArrival = b.gps_arrived_on || b.st_arrived_on;
                    
                    // If both have arrival times, sort chronologically
                    if (aArrival && bArrival) {
                      return new Date(aArrival) - new Date(bArrival);
                    }
                    
                    // If only one has arrival time, it comes first
                    if (aArrival && !bArrival) return -1;
                    if (!aArrival && bArrival) return 1;
                    
                    // If neither has arrival time, maintain original order
                    return 0;
                  }).map((assignment, index) => {
                    // Check if we're missing GPS arrival or departure data for this job
                    const missingGpsData = !assignment.gps_arrived_on || !assignment.gps_departure_on;
                    
                    // Find discrepancies for this specific job
                    const jobDiscrepancies = filteredDiscrepancies.filter(disc => {
                      // Check job_id match
                      if (disc.job_id && assignment.job_id && String(disc.job_id) === String(assignment.job_id)) {
                        return true;
                      }
                      // Check appointment_id match
                      if (disc.appointment_id && assignment.appointment_id && String(disc.appointment_id) === String(assignment.appointment_id)) {
                        return true;
                      }
                      // Check assignment_tracking_id match (if available)
                      if (disc.assignment_tracking_id && assignment.id && String(disc.assignment_tracking_id) === String(assignment.id)) {
                        return true;
                      }
                      return false;
                    });
                    
                    // Check for GPS departure vs ServiceTitan done time discrepancy (>10 minutes early)
                    const hasGpsDepartureDiscrepancy = (() => {
                      if (assignment.gps_departure_on && assignment.st_done_on) {
                        const gpsDeparture = new Date(assignment.gps_departure_on);
                        const stDone = new Date(assignment.st_done_on);
                        const timeDiffMinutes = (stDone - gpsDeparture) / (1000 * 60); // Difference in minutes
                        return timeDiffMinutes > 10; // GPS departure more than 10 minutes before ST done
                      }
                      return false;
                    })();
                    
                    return (
                      <div key={index} className="bg-white p-3 rounded border">
                        {/* Header: Job ID, Time, and Address */}
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className="flex items-center">
                                <span>
                                  {assignment.job_number 
                                    ? `Job #${assignment.job_number}` 
                                    : `Job ID ${assignment.job_id}`
                                  }
                                </span>
                              </div>
                              <div className="text-sm text-gray-500">
                                {assignment.st_scheduled_start && assignment.st_scheduled_end ? (() => {
                                  const startDate = new Date(assignment.st_scheduled_start);
                                  const endDate = new Date(assignment.st_scheduled_end);
                                  const workDate = new Date(employee_info.work_date + 'T12:00:00');
                                  
                                  // Check if appointment spans different days
                                  const startIsDifferentDay = startDate.toDateString() !== workDate.toDateString();
                                  const endIsDifferentDay = endDate.toDateString() !== workDate.toDateString();
                                  
                                  const startTime = startDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                                  const endTime = endDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                                  
                                  let displayText = `${startTime} - ${endTime}`;
                                  
                                  // Add date if different from work date
                                  if (startIsDifferentDay && endIsDifferentDay) {
                                    // Both times are on different days
                                    if (startDate.toDateString() === endDate.toDateString()) {
                                      // Same day but different from work date
                                      displayText += ` (${startDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' })})`;
                                    } else {
                                      // Different days
                                      displayText = `${startTime} (${startDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' })}) - ${endTime} (${endDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' })})`;
                                    }
                                  } else if (startIsDifferentDay) {
                                    // Only start time is different
                                    displayText = `${startTime} (${startDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' })}) - ${endTime}`;
                                  } else if (endIsDifferentDay) {
                                    // Only end time is different
                                    displayText = `${startTime} - ${endTime} (${endDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' })})`;
                                  }
                                  
                                  return displayText;
                                })() : 'N/A'}
                              </div>
                              
                              {/* Warning Badges */}
                              {hasGpsDepartureDiscrepancy && (
                                <div className="ml-3 bg-orange-100 border border-orange-300 rounded px-2 py-1 flex items-center w-fit">
                                  <AlertTriangle className="h-3 w-3 text-orange-600 mr-1.5 flex-shrink-0" />
                                  <span className="text-xs text-orange-800 font-medium">
                                    Closed Job {formatMinutesToHours(Math.round((new Date(assignment.st_done_on) - new Date(assignment.gps_departure_on)) / (1000 * 60)))} After Departure
                                  </span>
                                </div>
                              )}
                              {((assignment.st_arrived_on && !assignment.gps_arrived_on && assignment.st_arrival_distance_feet > 0) || 
                                (assignment.st_done_on && !assignment.gps_departure_on && assignment.st_done_distance_feet > 0)) && (
                                <div className="ml-3 bg-red-100 border border-red-300 rounded px-2 py-1 flex items-center w-fit">
                                  <AlertTriangle className="h-3 w-3 text-red-600 mr-1.5 flex-shrink-0" />
                                  <span className="text-xs text-red-800 font-medium">
                                    {(() => {
                                      // Check if it's an arrival issue (checkin)
                                      if (assignment.st_arrived_on && !assignment.gps_arrived_on && assignment.st_arrival_distance_feet > 0) {
                                        return 'ServiceTitan Checkin away from job';
                                      } 
                                      // Otherwise it's a departure issue (checkout)
                                      else if (assignment.st_done_on && !assignment.gps_departure_on && assignment.st_done_distance_feet > 0) {
                                        return 'ServiceTitan Checkout away from job';
                                      }
                                      return 'ServiceTitan away from job';
                                    })()}
                                  </span>
                                </div>
                              )}
                            </div>
                            {/* Arrival Window */}
                            <div className="text-sm pb-2 border-b border-gray-200">
                              <span className="text-gray-500">Arrival Window:</span>
                              <span className="ml-1 font-medium">
                                {assignment.st_arrival_window_start && assignment.st_arrival_window_end ? (() => {
                                  const startDate = new Date(assignment.st_arrival_window_start);
                                  const endDate = new Date(assignment.st_arrival_window_end);
                                  const workDate = new Date(employee_info.work_date + 'T12:00:00');
                                  
                                  // Check if arrival window spans different days
                                  const startIsDifferentDay = startDate.toDateString() !== workDate.toDateString();
                                  const endIsDifferentDay = endDate.toDateString() !== workDate.toDateString();
                                  
                                  const startTime = startDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                                  const endTime = endDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
                                  
                                  let displayText = `${startTime} - ${endTime}`;
                                  
                                  // Add date if different from work date
                                  if (startIsDifferentDay && endIsDifferentDay) {
                                    // Both times are on different days
                                    if (startDate.toDateString() === endDate.toDateString()) {
                                      // Same day but different from work date
                                      displayText += ` (${startDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' })})`;
                                    } else {
                                      // Different days
                                      displayText = `${startTime} (${startDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' })}) - ${endTime} (${endDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' })})`;
                                    }
                                  } else if (startIsDifferentDay) {
                                    // Only start time is different
                                    displayText = `${startTime} (${startDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' })}) - ${endTime}`;
                                  } else if (endIsDifferentDay) {
                                    // Only end time is different
                                    displayText = `${startTime} - ${endTime} (${endDate.toLocaleDateString([], { month: 'numeric', day: 'numeric' })})`;
                                  }
                                  
                                  return displayText;
                                })() : 'N/A'}
                              </span>
                            </div>
                          </div>
                          <div className="text-sm text-gray-600 text-right ml-4">
                            <div>
                              {assignment.job_address || 'No address'}
                            </div>
                            <div className="mt-1 text-xs">
                              {assignment.job_latitude && assignment.job_longitude ? (
                                <span className="text-green-600">
                                  📍 {parseFloat(assignment.job_latitude).toFixed(6)}, {parseFloat(assignment.job_longitude).toFixed(6)}
                                </span>
                              ) : (
                                <span className="text-red-500">
                                  ⚠️ No coordinates
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Actual Times - Left: Dispatch/Arrival, Right: Done/Departure */}
                        <div className="grid grid-cols-2 gap-6 text-sm mb-2">
                          {/* Left Column: Dispatch & Arrival (chronological order) */}
                          <div className="space-y-2">
                            {(() => {
                              const events = [];
                              
                              if (assignment.st_dispatched_on) {
                                events.push({
                                  time: new Date(assignment.st_dispatched_on),
                                  label: 'Dispatched',
                                  value: formatEventTime(assignment.st_dispatched_on, assignment.st_scheduled_start)
                                });
                              }
                              
                              if (assignment.gps_arrived_on) {
                                events.push({
                                  time: new Date(assignment.gps_arrived_on),
                                  label: 'Azuga Arrival',
                                  value: formatEventTime(assignment.gps_arrived_on, assignment.st_scheduled_start)
                                });
                              }
                              
                              if (assignment.st_arrived_on) {
                                events.push({
                                  time: new Date(assignment.st_arrived_on),
                                  label: 'ST Arrival',
                                  value: formatEventTime(assignment.st_arrived_on, assignment.st_scheduled_start),
                                  distance: assignment.st_arrival_distance_feet
                                });
                              }
                              
                              // Sort by time
                              events.sort((a, b) => a.time - b.time);
                              
                              // Add missing events
                              if (!assignment.st_dispatched_on) {
                                events.push({ label: 'Dispatched', value: 'N/A' });
                              }
                              if (!assignment.gps_arrived_on) {
                                events.push({ label: 'Azuga Arrival', value: 'N/A' });
                              }
                              if (!assignment.st_arrived_on) {
                                events.push({
                                  label: 'ST Arrival',
                                  value: 'N/A',
                                  distance: assignment.st_arrival_distance_feet
                                });
                              }
                              
                              return events.map((event, idx) => {
                                const isMissingGps = (event.label === 'Azuga Arrival' && event.value === 'N/A') ||
                                                   (event.label === 'Azuga Departure' && event.value === 'N/A');
                                const isStArrivalWithoutGps = (event.label === 'ST Arrival' && event.value !== 'N/A' && !assignment.gps_arrived_on);
                                
                                return (
                                  <div key={idx} className={`flex justify-between items-center`}>
                                    <span>
                                      {event.label}:
                                    </span>
                                    <span className={`ml-1 font-medium ${
                                      isMissingGps ? 'bg-yellow-50 px-2 py-1 rounded border border-yellow-200 text-yellow-700 font-medium' : ''
                                    }`}>
                                      {event.value}
                                      {event.distance != null && (
                                        <span className={`ml-1 ${
                                          isStArrivalWithoutGps ? 'bg-yellow-50 px-2 py-1 rounded border border-yellow-200 text-yellow-700 font-medium' : 'text-gray-400'
                                        }`}>
                                          ({formatDistance(event.distance)})
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                          
                          {/* Right Column: Done & Departure (chronological order) */}
                          <div className="space-y-2">
                            {(() => {
                              const events = [];
                              
                              if (assignment.st_done_on) {
                                events.push({
                                  time: new Date(assignment.st_done_on),
                                  label: 'ST Done',
                                  value: formatEventTime(assignment.st_done_on, assignment.st_scheduled_start),
                                  distance: assignment.st_done_distance_feet
                                });
                              }
                              
                              if (assignment.gps_departure_on) {
                                events.push({
                                  time: new Date(assignment.gps_departure_on),
                                  label: 'Azuga Departure',
                                  value: formatEventTime(assignment.gps_departure_on, assignment.st_scheduled_start)
                                });
                              }
                              
                              // Sort by time
                              events.sort((a, b) => a.time - b.time);
                              

                              
                              // Add missing events
                              if (!assignment.st_done_on) {
                                events.push({
                                  label: 'ST Done',
                                  value: 'N/A',
                                  distance: assignment.st_done_distance_feet
                                });
                              }
                              if (!assignment.gps_departure_on) {
                                events.push({ label: 'Azuga Departure', value: 'N/A' });
                              }
                              
                              const eventElements = events.map((event, idx) => {
                                const shouldFlag = hasGpsDepartureDiscrepancy && 
                                  (event.label === 'Azuga Departure' || event.label === 'ST Done');
                                const isMissingGps = (event.label === 'Azuga Departure' && event.value === 'N/A');
                                const isStDoneWithoutGps = (event.label === 'ST Done' && event.value !== 'N/A' && !assignment.gps_departure_on);
                                
                                return (
                                  <div key={idx} className={`flex justify-between items-center`}>
                                    <span>
                                      {event.label}:
                                    </span>
                                    <span className='ml-1 font-medium'>
                                      <span className={`${
                                        (shouldFlag || isMissingGps) ? 'bg-yellow-50 px-2 py-1 rounded border border-yellow-200 text-yellow-700 font-medium' : ''
                                      }`}>
                                        {event.value}
                                      </span>
                                      {event.distance != null && (
                                        <span className={`ml-1 ${
                                          isStDoneWithoutGps ? 'bg-yellow-50 px-2 py-1 rounded border border-yellow-200 text-yellow-700 font-medium' : 'text-gray-400'
                                        }`}>
                                          ({formatDistance(event.distance)})
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                );
                              });
                              

                              
                              return eventElements;
                            })()}
                          </div>
                        </div>

                        {/* Discrepancies for this job */}
                        {jobDiscrepancies.length > 0 && (
                          <div className="border-t pt-3 mb-2">
                            <div className="space-y-2">
                              {jobDiscrepancies.map((disc, discIdx) => (
                                <div key={discIdx} className={`text-sm p-3 rounded border-l-4 ${
                                  disc.severity_level === 'critical' ? 'bg-red-50 border-red-400 text-red-800' :
                                  disc.severity_level === 'high' ? 'bg-orange-50 border-orange-400 text-orange-800' :
                                  disc.severity_level === 'medium' ? 'bg-yellow-50 border-yellow-400 text-yellow-800' :
                                  'bg-blue-50 border-blue-400 text-blue-800'
                                }`}>
                                  <div className="font-medium mb-1">
                                    {disc.discrepancy_type} ({disc.severity_level})
                                  </div>
                                  {!['excessive_idle_time', 'checkin', 'checkout', 'early_checkin', 'late_checkout'].includes(disc.discrepancy_type) && (
                                    <>
                                      <div className="text-sm">
                                        {disc.description}
                                      </div>
                                      {disc.time_variance_minutes && (
                                        <div className="text-xs mt-1 font-medium">
                                          Time Variance: {formatMinutesToHours(Math.abs(disc.time_variance_minutes))}
                                        </div>
                                      )}
                                      {disc.distance_feet && (
                                        <div className="text-xs mt-1">
                                          Distance: {formatDistance(disc.distance_feet)}
                                        </div>
                                      )}
                                    </>
                                  )}
                                  {/* Show idle time details for excessive idle time */}
                                  {disc.discrepancy_type === 'excessive_idle_time' && disc.context_data && (
                                    <div className="text-xs mt-2">
                                      <div className="flex justify-between items-start">
                                        <div className="space-y-1">
                                          {disc.context_data.idle_duration_minutes && (
                                            <div className="text-gray-600 font-medium">
                                              Idle Time: {formatMinutesToHours(Math.round(disc.context_data.idle_duration_minutes))}
                                            </div>
                                          )}
                                          {disc.context_data.idle_start_time && disc.context_data.idle_end_time && (
                                            <div className="text-gray-600">
                                              {formatTime(new Date(disc.context_data.idle_start_time))} - {formatTime(new Date(disc.context_data.idle_end_time))}
                                            </div>
                                          )}
                                          {disc.distance_feet && (
                                            <div className="text-gray-600">
                                              {formatDistance(disc.distance_feet)} from job site
                                            </div>
                                          )}
                                        </div>
                                        <div className="ml-4 text-right space-y-1">
                                          {(disc.context_data.idle_address || disc.context_data.idle_location) && (
                                            <div className="text-gray-600">
                                              📍 {disc.context_data.idle_address || disc.context_data.idle_location}
                                            </div>
                                          )}
                                          {disc.context_data.idle_latitude && disc.context_data.idle_longitude && (
                                            <div className="text-gray-600">
                                              🗺️ <a 
                                                href={`https://www.google.com/maps?q=${disc.context_data.idle_latitude},${disc.context_data.idle_longitude}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 underline"
                                              >
                                                {parseFloat(disc.context_data.idle_latitude).toFixed(6)}, {parseFloat(disc.context_data.idle_longitude).toFixed(6)}
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Show checkin/checkout exception details */}
                                  {['checkin', 'checkout', 'early_checkin', 'late_checkout'].includes(disc.discrepancy_type) && (
                                    <div className="text-xs mt-2 space-y-1">
                                      {disc.actual_time && (
                                        <div className="text-gray-600 font-medium">
                                          {disc.discrepancy_type === 'checkin' || disc.discrepancy_type === 'early_checkin' ? 'Check-in' : 'Check-out'}: {formatTime(disc.actual_time)}
                                        </div>
                                      )}
                                      {disc.expected_time && (
                                        <div className="text-gray-600">
                                          Expected: {formatTime(disc.expected_time)}
                                        </div>
                                      )}
                                      {disc.time_variance_minutes && (
                                        <div className="text-gray-600">
                                          Time Difference: {formatMinutesToHours(Math.abs(disc.time_variance_minutes))}
                                        </div>
                                      )}
                                      {disc.distance_feet && (
                                        <div className="text-gray-600">
                                          {formatDistance(disc.distance_feet)} from job site
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Job Summary */}
                        {assignment.job_summary && (
                          <div className="text-sm text-gray-600 w-full mt-2 pt-2 border-t border-gray-200">
                            <div className="font-medium text-gray-700 mb-1">Job Summary:</div>
                            <div className="whitespace-pre-wrap">
                              {createTeaser(assignment.job_summary, 2000)}
                            </div>
                          </div>
                        )}
                      </div>
                                          );
                    })}
                  </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No job assignments found for this technician</p>
                </div>
              )}
            </div>

            {/* Non-Job Appointments */}
            {technicianData.non_job_appointments && (
              technicianData.non_job_appointments.all_appointments && 
              technicianData.non_job_appointments.all_appointments.length > 0
            ) && (
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <Calendar className="h-5 w-5 mr-2 text-purple-500" />
                  Non-Job Appointments
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Travel Appointments */}
                  {technicianData.non_job_appointments.travel_appointments && 
                   technicianData.non_job_appointments.travel_appointments.length > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                      <h4 className="font-medium text-purple-800 mb-3 flex items-center">
                        <Truck className="h-4 w-4 mr-2 text-purple-600" />
                        Travel Appointments ({technicianData.non_job_appointments.travel_appointments.length})
                      </h4>
                      <div className="space-y-2">
                        {technicianData.non_job_appointments.travel_appointments.map((appointment, index) => (
                          <div key={index} className="text-sm p-2 bg-purple-50 rounded border-l-2 border-purple-400">
                            <div className="font-medium text-purple-900">{appointment.name}</div>
                            <div className="text-purple-700">
                              {appointment.start_time && (
                                <span>
                                  {new Date(appointment.start_time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                </span>
                              )}
                              {appointment.duration && (
                                <span className="ml-2">
                                  ({appointment.duration} min)
                                </span>
                              )}
                            </div>
                            {appointment.summary && (
                              <div className="text-xs text-purple-600 mt-1">{appointment.summary}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Non-Travel Appointments */}
                  {technicianData.non_job_appointments.non_travel_appointments && 
                   technicianData.non_job_appointments.non_travel_appointments.length > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-blue-200">
                      <h4 className="font-medium text-blue-800 mb-3 flex items-center">
                        <span className="mr-2 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Virtual/Remote</span>
                        Non-Travel Appointments ({technicianData.non_job_appointments.non_travel_appointments.length})
                      </h4>
                      <div className="space-y-2">
                        {technicianData.non_job_appointments.non_travel_appointments.map((appointment, index) => (
                          <div key={index} className="text-sm p-2 bg-blue-50 rounded border-l-2 border-blue-400">
                            <div className="font-medium text-blue-900">{appointment.name}</div>
                            <div className="text-blue-700">
                              {appointment.start_time && (
                                <span>
                                  {new Date(appointment.start_time).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                </span>
                              )}
                              {appointment.duration && (
                                <span className="ml-2">
                                  ({appointment.duration} min)
                                </span>
                              )}
                            </div>
                            {appointment.summary && (
                              <div className="text-xs text-blue-600 mt-1">{appointment.summary}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Summary */}
                <div className="mt-4 pt-3 border-t border-purple-200">
                  <div className="text-sm text-purple-700">
                    <span className="font-medium">Total Appointments:</span> {technicianData.non_job_appointments.all_appointments.length} 
                    {technicianData.non_job_appointments.travel_appointments && technicianData.non_job_appointments.travel_appointments.length > 0 && (
                      <span className="ml-2">
                        • <span className="font-medium">Travel:</span> {technicianData.non_job_appointments.travel_appointments.length}
                      </span>
                    )}
                    {technicianData.non_job_appointments.non_travel_appointments && technicianData.non_job_appointments.non_travel_appointments.length > 0 && (
                      <span className="ml-2">
                        • <span className="font-medium">Non-Travel:</span> {technicianData.non_job_appointments.non_travel_appointments.length}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}


          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add pagination controls component
  const Pagination = () => {
    const totalPages = Math.ceil(totalEmployees / pageSize);
    
    // Show max 5 page numbers, centered around current page
    const getPageNumbers = () => {
      let pages = [];
      const maxPages = 5;
      let startPage = Math.max(1, page - Math.floor(maxPages / 2));
      let endPage = Math.min(totalPages, startPage + maxPages - 1);
      
      if (endPage - startPage + 1 < maxPages) {
        startPage = Math.max(1, endPage - maxPages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      return pages;
    };

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
            disabled={page === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Previous
          </button>
          <button
            onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
            disabled={page === totalPages}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{((page - 1) * pageSize) + 1}</span> to{' '}
              <span className="font-medium">{Math.min(page * pageSize, totalEmployees)}</span> of{' '}
              <span className="font-medium">{totalEmployees}</span> results
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                disabled={page === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-1 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
              >
                Previous
              </button>
              {/* First page if not in range */}
              {!getPageNumbers().includes(1) && (
                <>
                  <button
                    onClick={() => setPage(1)}
                    className="relative inline-flex items-center px-2 py-1 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    1
                  </button>
                  <span className="relative inline-flex items-center px-2 py-1 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                    ...
                  </span>
                </>
              )}
              {/* Page numbers */}
              {getPageNumbers().map(num => (
                <button
                  key={num}
                  onClick={() => setPage(num)}
                  className={`relative inline-flex items-center px-2 py-1 text-sm font-semibold ${
                    page === num
                      ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                      : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                  }`}
                >
                  {num}
                </button>
              ))}
              {/* Last page if not in range */}
              {!getPageNumbers().includes(totalPages) && (
                <>
                  <span className="relative inline-flex items-center px-2 py-1 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                    ...
                  </span>
                  <button
                    onClick={() => setPage(totalPages)}
                    className="relative inline-flex items-center px-2 py-1 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                disabled={page === totalPages}
                className="relative inline-flex items-center rounded-r-md px-2 py-1 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  // Remove the EmployeeRow component and revert to original employee table structure
  // ... existing code ...

  {/* Employee Table */}
  <table className="min-w-full divide-y divide-gray-200">
    <thead className="bg-gray-50">
      <tr>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Employee
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Contact Info
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          Department/Role
        </th>
        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
          System Presence
        </th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200">
      {filteredEmployees.map((employee, index) => {
        // Simple mapping - use the data as provided by the API
        const empData = {
          id: employee.id || 'N/A',
          employee_id: employee.employee_id || 'N/A',
          name: employee.full_name || `Employee ${employee.id}`,
          first_name: employee.first_name || '',
          last_name: employee.last_name || '',
          email: employee.email || 'No email',
          phone: employee.phone || 'No phone',
          department: employee.department || 'No department',
          job_title: employee.job_title || 'No title',
          hire_date: employee.hire_date || null
        };

        // System presence - use the structured data from API
        const systemPresence = employee.system_presence || {};
        const activeStatus = employee.active_status || {};
        
        const getInitials = (name) => {
          if (!name || name === 'N/A') return '?';
          return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
        };

        return (
          <tr key={employee.id || index} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10">
                  <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-500 text-white text-sm font-medium">
                    {getInitials(empData.name)}
                  </span>
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-gray-900">
                    {empData.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    ID: {empData.id}
                  </div>
                </div>
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-900">
                {empData.email}
              </div>
              <div className="text-sm text-gray-500">
                {empData.phone}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-900">
                {empData.department}
              </div>
              <div className="text-sm text-gray-500">
                {empData.job_title}
              </div>
              {empData.hire_date && (
                <div className="text-xs text-gray-400 mt-1">
                  Hired: {new Date(empData.hire_date).toLocaleDateString()}
                </div>
              )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="flex flex-col space-y-2">
                <div className={`px-3 py-1.5 rounded text-xs font-medium flex items-center justify-between ${
                  systemPresence.servicetitan ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  <span>ServiceTitan</span>
                  {systemPresence.servicetitan && <Check className="h-3 w-3" />}
                </div>
                <div className={`px-3 py-1.5 rounded text-xs font-medium flex items-center justify-between ${
                  systemPresence.adp ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  <span>ADP</span>
                  {systemPresence.adp && <Check className="h-3 w-3" />}
                </div>
                <div className={`px-3 py-1.5 rounded text-xs font-medium flex items-center justify-between ${
                  systemPresence.azuga ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-600'
                }`}>
                  <span>Azuga</span>
                  {systemPresence.azuga && <Check className="h-3 w-3" />}
                </div>
              </div>
            </td>
          </tr>
        );
      })}
      {filteredEmployees.length === 0 && (
        <tr>
          <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
            <div className="flex flex-col items-center">
              <Users className="h-12 w-12 text-gray-400 mb-2" />
              {employeeSearch ? (
                <>
                  <span>No employees found matching "{employeeSearch}"</span>
                  <span className="text-sm">Try a different search term</span>
                </>
              ) : (
                <>
                  <span>No employee data available</span>
                  <span className="text-sm">Try refreshing the data</span>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </tbody>
  </table>

  if (dashboardLoading && activeTab === 'dashboard') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading dashboard data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Time Analysis</h1>
          <p className="text-gray-600">Business Analytics & Insights</p>
          {refreshInProgress && refreshDetails && (
            <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-center text-sm text-orange-800">
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                <span className="font-medium">Gold layer refresh in progress</span>
              </div>
              <div className="mt-1 text-xs text-orange-700 space-y-1">
                <div>Operation: {formatOperationType(refreshDetails.operation_type)}</div>
                {refreshDetails.start_date && (
                  <div>Date Range: {refreshDetails.start_date}{refreshDetails.end_date && refreshDetails.end_date !== refreshDetails.start_date ? ` to ${refreshDetails.end_date}` : ''}</div>
                )}
                {refreshDetails.components && (
                  <div>Components: {refreshDetails.components.join(', ')}</div>
                )}
                <div>Started: {new Date(refreshDetails.start_time).toLocaleTimeString()} ({formatDuration(refreshDetails.start_time)} ago)</div>
              </div>
            </div>
          )}
        </div>
        {isAdmin() && (
          <button
            onClick={clearCache}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            title="Clear all cache to see fresh data"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Cache
          </button>
        )}
      </div>



      {/* Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`mr-8 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('employees')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'employees'
                ? 'border-yellow-500 text-yellow-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Employees
          </button>
        </nav>
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Refresh Controls for Dashboard */}
          {isAdmin() && (
            <div className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg">
              <div className="flex items-center space-x-4">
                {/* Refresh Button */}
                <button
                  onClick={() => refreshGoldLayer()}
                  disabled={refreshing || refreshInProgress}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={`Refresh data for ${formatDateForAPI(refreshStartDate)}${refreshEndDate !== refreshStartDate ? ` to ${formatDateForAPI(refreshEndDate)}` : ''}`}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${refreshing || refreshInProgress ? 'animate-spin' : ''}`} />
                  {refreshing || refreshInProgress ? 'Refreshing...' : 'Refresh from SIlver'}
                </button>
                
                {/* Date Inputs */}
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <DatePicker
                      selected={refreshStartDate}
                      onChange={date => setRefreshStartDate(date)}
                      dateFormat="yyyy-MM-dd"
                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholderText="Start Date"
                    />
                  </div>
                  <span className="text-gray-400">to</span>
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <DatePicker
                      selected={refreshEndDate}
                      onChange={date => setRefreshEndDate(date)}
                      dateFormat="yyyy-MM-dd"
                      className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholderText="End Date"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Dashboard Filters */}
          <div className="p-0">
            {/* Global Filters */}
            <div className="mb-6 flex flex-wrap gap-4 items-center bg-white p-4 rounded-lg shadow">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-gray-400" />
                <DatePicker
                  selected={globalFilters.date}
                  onChange={date => updateGlobalFilter('date', date)}
                  dateFormat="yyyy-MM-dd"
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center space-x-2">
                <Building className="h-5 w-5 text-gray-400" />
                <select
                  value={globalFilters.work_location || globalFilters.tenant}
                  onChange={e => {
                    const selectedValue = e.target.value;
                    // Check if this is a work location (sub-item) for RNC
                    const isRncWorkLocation = availableTenants.some(tenant => 
                      tenant.tenant_location === 'DelAir RNC' && 
                      tenant.work_locations && 
                      tenant.work_locations.includes(selectedValue)
                    );
                    
                    // Update both tenant and work_location in a single state update to avoid batching issues
                    setGlobalFilters(prev => {
                      const newFilters = {
                        ...prev,
                        tenant: isRncWorkLocation ? 'DelAir RNC' : selectedValue,
                        work_location: isRncWorkLocation ? selectedValue : ''
                      };
                      
                      // Save to localStorage
                      try {
                        localStorage.setItem('goldLayerFilters', JSON.stringify({
                          date: newFilters.date.toISOString(),
                          tenant: newFilters.tenant,
                          work_location: newFilters.work_location,
                          exclude_apprentices: newFilters.exclude_apprentices
                        }));
                      } catch (error) {
                        console.error('Error saving filters to localStorage:', error);
                      }
                      
                      return newFilters;
                    });
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Tenants</option>
                  {availableTenants.map(tenant => (
                    <React.Fragment key={tenant.tenant_location}>
                      {/* Main tenant option */}
                      <option value={tenant.tenant_location}>
                        {tenant.tenant_location}
                      </option>
                      {/* Work location sub-options for RNC */}
                      {tenant.tenant_location === 'DelAir RNC' && tenant.work_locations && tenant.work_locations.length > 0 && 
                        tenant.work_locations.map(workLocation => (
                          <option key={`${tenant.tenant_location}-${workLocation}`} value={workLocation}>
                            &nbsp;&nbsp;&nbsp;&nbsp;↳ {workLocation}
                          </option>
                        ))
                      }
                    </React.Fragment>
                  ))}
                </select>
              </div>
              {(globalFilters.tenant) && (
                <button
                  onClick={() => {
                    setGlobalFilters(prev => {
                      const newFilters = {
                        ...prev,
                        tenant: '',
                        work_location: ''
                      };
                      
                      // Save to localStorage
                      try {
                        localStorage.setItem('goldLayerFilters', JSON.stringify({
                          date: newFilters.date.toISOString(),
                          tenant: newFilters.tenant,
                          work_location: newFilters.work_location,
                          exclude_apprentices: newFilters.exclude_apprentices
                        }));
                      } catch (error) {
                        console.error('Error saving filters to localStorage:', error);
                      }
                      
                      return newFilters;
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  title="Clear tenant filter"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="exclude-apprentices"
                  checked={globalFilters.exclude_apprentices || false}
                  onChange={(e) => updateGlobalFilter('exclude_apprentices', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="exclude-apprentices" className="text-sm text-gray-700 cursor-pointer">
                  Exclude Apprentices
                </label>
              </div>
              <div className="flex items-center space-x-2 ml-auto">
                <button
                  onClick={clearSavedFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                  title="Reset filters to default values"
                >
                  Reset Filters
                </button>
              </div>
            </div>

            {/* Technician Hours Summary */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Technician Hours Summary</h3>
                <p className="text-sm text-gray-600">
                  PAY hours vs actual determined hours for {formatDate(globalFilters.date)}
                  {globalFilters.tenant && ` - ${globalFilters.tenant}`}
                </p>
              </div>

              {technicianDataLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading technician data...</p>
                </div>
              ) : technicianData.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No technician data found for the selected date and tenant</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Rank
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Technician
                        </th>

                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          PAY HOURS
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actual Hours
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Variance
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Viewed
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {technicianData.sort((a, b) => Math.abs(b.variance || 0) - Math.abs(a.variance || 0)).map((tech, index) => (
                        <tr 
                          key={tech.employee_id} 
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => openTechnicianDetailModal(tech)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full text-xs font-medium ${
                              index === 0 ? 'bg-red-100 text-red-800' :
                              index === 1 ? 'bg-orange-100 text-orange-800' :
                              index === 2 ? 'bg-yellow-50 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              #{index + 1}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-between w-full">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {tech.employee_name}
                                  {tech.work_location && tech.tenant_location === 'DelAir RNC' && (
                                    <>
                                      <span className="mx-2 text-gray-400">•</span>
                                      <span className="text-purple-600 font-medium">{tech.work_location}</span>
                                    </>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {tech.job_title || 'N/A'}
                                </div>
                                <div className="text-sm text-gray-500 flex items-center mt-1">
                                  <User className="h-4 w-4 mr-1 text-gray-600" />
                                  {tech.employee_id}
                                  {tech.vehicle_name && (
                                    <>
                                      <span className="mx-2">•</span>
                                      <span className="text-blue-600 flex items-center">
                                        <Truck className="h-4 w-4 mr-1" />
                                        {tech.vehicle_name}
                                      </span>
                                    </>
                                  )}
                                  {!tech.vehicle_name && (
                                    <>
                                      <span className="mx-2">•</span>
                                      <span className="text-red-600 flex items-center">
                                        <AlertTriangle className="h-4 w-4 mr-1" />
                                        No Azuga Vehicle
                                      </span>
                                    </>
                                  )}
                                  {tech.breadcrumb_status === "no_job_breadcrumbs" && tech.vehicle_name && (
                                    <>
                                      <span className="mx-2">•</span>
                                      <span className="text-orange-600 flex items-center">
                                        <MapPin className="h-4 w-4 mr-1" />
                                        No Job Breadcrumbs
                                      </span>
                                    </>
                                  )}
                                  {tech.breadcrumb_status === "no_breadcrumbs" && tech.vehicle_name && (
                                    <>
                                      <span className="mx-2">•</span>
                                      <span className="text-yellow-600 flex items-center">
                                        <MapPin className="h-4 w-4 mr-1" />
                                        No GPS Data
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              {/* Commute Information - positioned to the right */}
                              {(() => {
                                const morningCommute = tech.commute_data?.morning_commute;
                                const eveningCommute = tech.commute_data?.evening_commute;
                                const isDoorToDoorDay = tech.commute_data?.is_door_to_door_day;
                                
                                // Use generalized function for morning drive time calculation
                                const morningPaidDriveTime = calculateMorningDriveTime(tech, null, { tenant_code: tech.tenant_code });
                                
                                // Check if end_of_day is an office arrival WITH NO DEPARTURE (if so, don't include evening drive time)
                                const isEndOfDayOfficeArrivalWithNoDeparture = (() => {
                                  if (!tech.billing_analysis?.end_of_day || !tech.location_arrivals) {
                                    return false;
                                  }
                                  
                                  try {
                                    const endOfDayTime = new Date(tech.billing_analysis.end_of_day).getTime();
                                    const locationData = typeof tech.location_arrivals === 'string' 
                                      ? JSON.parse(tech.location_arrivals) 
                                      : tech.location_arrivals;
                                    
                                    if (locationData && locationData.all_visits && Array.isArray(locationData.all_visits)) {
                                      return locationData.all_visits.some(visit => {
                                        if (visit.location_type === 'office' && visit.arrival_time) {
                                          const arrivalTime = new Date(visit.arrival_time).getTime();
                                          const timeDiff = Math.abs(endOfDayTime - arrivalTime);
                                          
                                          // Check if end_of_day matches this office arrival (within 5 minutes)
                                          if (timeDiff <= 300000) {
                                            // Only exclude evening drive time if there's NO departure time (they stayed at office)
                                            return !visit.departure_time || visit.departure_time === null;
                                          }
                                        }
                                        return false;
                                      });
                                    }
                                  } catch (error) {
                                    console.error('Error checking end_of_day office arrival with no departure:', error);
                                  }
                                  return false;
                                })();
                                
                                const eveningAzugaTime = eveningCommute?.minutes || 0;
                                const eveningGoogleTime = eveningCommute?.expected_duration_minutes || 0;
                                const eveningDriveTime = isEndOfDayOfficeArrivalWithNoDeparture ? 0 : Math.min(eveningAzugaTime, eveningGoogleTime);
                                
                                // For door-to-door days (Sundays/holidays), don't subtract 45 minutes
                                const eveningPaidDriveTime = isDoorToDoorDay ? eveningDriveTime : Math.max(0, eveningDriveTime - 45);
                                
                                const hasCommute = morningPaidDriveTime > 0 || eveningPaidDriveTime > 0;
                                
                                                                 return hasCommute && (
                                   <div className="text-right ml-4">
                                     <div className="text-blue-600 flex items-center justify-end text-sm">
                                       🚗&nbsp;&nbsp;Paid Drive time: {(() => {
                                        const totalMinutes = morningPaidDriveTime + eveningPaidDriveTime;
                                        const hours = Math.floor(totalMinutes / 60);
                                        const minutes = totalMinutes % 60;
                                        if (hours > 0 && minutes > 0) {
                                          return `${hours}h${minutes}m`;
                                        } else if (hours > 0) {
                                          return `${hours}h`;
                                        } else {
                                          return `${minutes}m`;
                                        }
                                      })()}
                                    </div>
                                    <div className="text-gray-500 text-xs">
                                      ({
                                        [
                                          morningPaidDriveTime > 0 && `${(() => {
                                            const hours = Math.floor(morningPaidDriveTime / 60);
                                            const minutes = morningPaidDriveTime % 60;
                                            if (hours > 0 && minutes > 0) {
                                              return `${hours}h${minutes}m`;
                                            } else if (hours > 0) {
                                              return `${hours}h`;
                                            } else {
                                              return `${minutes}m`;
                                            }
                                          })()} morning`,
                                          eveningPaidDriveTime > 0 && `${(() => {
                                            const hours = Math.floor(eveningPaidDriveTime / 60);
                                            const minutes = eveningPaidDriveTime % 60;
                                            if (hours > 0 && minutes > 0) {
                                              return `${hours}h${minutes}m`;
                                            } else if (hours > 0) {
                                              return `${hours}h`;
                                            } else {
                                              return `${minutes}m`;
                                            }
                                          })()} evening`
                                        ].filter(Boolean).join(', ')
                                      })
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {(() => {
                                const adpHours = tech.adp_hours || 0;
                                const tfdaHours = tech.tfda_hours || 0;
                                
                                if (adpHours > 0 && tfdaHours > 0) {
                                  // Both systems used
                                  return (
                                    <div>
                                      <div>{formatMinutesToHours(Math.round(adpHours * 60))}</div>
                                      <div className="text-xs text-gray-600">{formatMinutesToHours(Math.round(tfdaHours * 60))} <span className="text-gray-400">(TFDA)</span></div>
                                    </div>
                                  );
                                } else if (adpHours > 0) {
                                  // Only ADP used - no label
                                  return formatMinutesToHours(Math.round(adpHours * 60));
                                } else if (tfdaHours > 0) {
                                  // Only TFDA used - show (TFDA) label
                                  return (
                                    <span>
                                      {formatMinutesToHours(Math.round(tfdaHours * 60))} <span className="text-gray-400">(TFDA)</span>
                                    </span>
                                  );
                                } else {
                                  // No hours recorded
                                  return '0:00';
                                }
                              })()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatMinutesToHours(Math.round((tech.actual_hours || 0) * 60))}
                            </div>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              const varianceHours = tech.variance || 0;
                              const varianceMinutes = Math.abs(Math.round(varianceHours * 60));
                              let varianceColorClass;
                              
                              if (varianceHours === 0) {
                                // Perfect match - green
                                varianceColorClass = 'text-green-600';
                              } else if (varianceHours > 0) {
                                // Positive variance (overbilling) - always a problem, use warning colors
                                if (varianceMinutes > 300) varianceColorClass = 'text-red-600';
                                else if (varianceMinutes > 180) varianceColorClass = 'text-orange-600';
                                else if (varianceMinutes > 60) varianceColorClass = 'text-yellow-600';
                                else varianceColorClass = 'text-yellow-500'; // Light warning for minor overbilling
                              } else {
                                // Negative variance (underbilling) - use standard severity colors
                                if (varianceMinutes > 300) varianceColorClass = 'text-red-600';
                                else if (varianceMinutes > 180) varianceColorClass = 'text-orange-600';
                                else if (varianceMinutes > 60) varianceColorClass = 'text-yellow-600';
                                else varianceColorClass = 'text-blue-600'; // Minor underbilling
                              }
                              
                              return (
                                <div className={`font-bold text-lg ${varianceColorClass}`}>
                                  {tech.variance > 0 ? '+' : tech.variance < 0 ? '' : ''}{formatMinutesToHours(Math.abs(Math.round((tech.variance || 0) * 60)))}
                                </div>
                              );
                            })()}
                            <div className="text-sm text-gray-500">
                              {tech.variance > 0 ? 'overpaid' : tech.variance < 0 ? 'underpaid' : 'match'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {tech.recent_views && tech.recent_views.length > 0 ? (
                              <div className="space-y-1">
                                {tech.recent_views.slice(0, 2).map((view, idx) => (
                                  <div key={idx} className="text-xs">
                                    <div className="font-medium text-gray-900">{view.user_name}</div>
                                    <div className="text-gray-500">
                                      {new Date(view.last_viewed_at).toLocaleString([], {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </div>
                                  </div>
                                ))}
                                {tech.recent_views.length > 2 && (
                                  <div className="text-xs text-gray-400 italic">
                                    +{tech.recent_views.length - 2} more
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400 italic">Not viewed</div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Employees Tab - Fixed implementation */}
      {activeTab === 'employees' && (
        <div className="space-y-6">
          {/* Refresh Controls for Employees */}
          {isAdmin() && (
            <div className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg">
              <div className="flex items-center space-x-4">
                {/* Employee Master Refresh Button */}
                <button
                  onClick={refreshEmployeeMaster}
                  disabled={refreshing || refreshInProgress}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Refresh employee master data"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${refreshing || refreshInProgress ? 'animate-spin' : ''}`} />
                  {refreshing || refreshInProgress ? 'Refreshing...' : 'Refresh Employee Master'}
                </button>
              </div>
            </div>
          )}

            {/* Employee Summary Cards - using dashboard summary data */}
            {dashboardLoading ? (
              <div className="space-y-6">
                {/* First row - 2 columns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow border-l-4 border-blue-500 p-6">
                    <div className="flex items-center justify-between">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                        <div className="h-8 bg-gray-200 rounded w-16"></div>
                      </div>
                      <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow border-l-4 border-green-500 p-6">
                    <div className="flex items-center justify-between">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                        <div className="h-8 bg-gray-200 rounded w-16"></div>
                      </div>
                      <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>
                {/* Second row - 3 columns */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-lg shadow border-l-4 border-yellow-500 p-6">
                    <div className="flex items-center justify-between">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                        <div className="h-8 bg-gray-200 rounded w-16"></div>
                      </div>
                      <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow border-l-4 border-orange-500 p-6">
                    <div className="flex items-center justify-between">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                        <div className="h-8 bg-gray-200 rounded w-16"></div>
                      </div>
                      <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg shadow border-l-4 border-purple-500 p-6">
                    <div className="flex items-center justify-between">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                        <div className="h-8 bg-gray-200 rounded w-16"></div>
                      </div>
                      <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                  </div>
                </div>
              </div>
            ) : dashboard?.summary && (
            <div className="space-y-6">
              {/* First row - 2 columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow border-l-4 border-blue-500 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Total Employees</h3>
                      <p className="text-2xl font-bold text-blue-600">
                        {dashboard.summary.employees?.total || 0}
                      </p>
                    </div>
                    <Users className="h-8 w-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow border-l-4 border-green-500 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">In All Systems</h3>
                      <p className="text-2xl font-bold text-green-600">
                        {dashboard.summary.employees?.in_all_systems || 0}
                      </p>
                    </div>
                    <Award className="h-8 w-8 text-green-500" />
                  </div>
                </div>
              </div>

              {/* Second row - 3 columns */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow border-l-4 border-yellow-500 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">ServiceTitan</h3>
                      <p className="text-2xl font-bold text-yellow-600">
                        {dashboard.summary.employees?.in_servicetitan || 0}
                      </p>
                    </div>
                    <Car className="h-8 w-8 text-yellow-500" />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow border-l-4 border-orange-500 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Azuga</h3>
                      <p className="text-2xl font-bold text-orange-600">
                        {dashboard.summary.employees?.in_azuga || 0}
                      </p>
                    </div>
                    <Truck className="h-8 w-8 text-orange-500" />
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow border-l-4 border-purple-500 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">ADP</h3>
                      <p className="text-2xl font-bold text-purple-600">
                        {dashboard.summary.employees?.in_adp || 0}
                      </p>
                    </div>
                    <Timer className="h-8 w-8 text-purple-500" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Employee Table with Pagination */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Employee Master Records</h3>
                  <p className="text-sm text-gray-600">
                    {employeeSearch 
                      ? `Showing results for "${employeeSearch}"`
                      : `Showing all employees`
                    }
                  </p>
                </div>
                <div className="flex items-center max-w-md w-full sm:w-96">
                  <div className="relative w-full">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search employees..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setSearchTerm('');
                        }
                      }}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    {searchTerm && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <button
                          onClick={() => setSearchTerm('')}
                          className="text-gray-400 hover:text-gray-500 focus:outline-none"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Table content */}
            <div className="overflow-x-auto">
              {employeesLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Loading employees...</span>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employee
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Department/Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        System Presence
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEmployees.map((employee, index) => {
                      // Simple mapping - use the data as provided by the API
                      const empData = {
                        id: employee.id || 'N/A',
                        employee_id: employee.employee_id || 'N/A',
                        name: employee.full_name || `Employee ${employee.id}`,
                        first_name: employee.first_name || '',
                        last_name: employee.last_name || '',
                        email: employee.email || 'No email',
                        phone: employee.phone || 'No phone',
                        department: employee.department || 'No department',
                        job_title: employee.job_title || 'No title',
                        hire_date: employee.hire_date || null
                      };

                      // System presence - use the structured data from API
                      const systemPresence = employee.system_presence || {};
                      const activeStatus = employee.active_status || {};
                      
                      const getInitials = (name) => {
                        if (!name || name === 'N/A') return '?';
                        return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
                      };

                      return (
                        <tr key={employee.id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-500 text-white text-sm font-medium">
                                  {getInitials(empData.name)}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {empData.name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  ID: {empData.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {empData.email}
                            </div>
                            <div className="text-sm text-gray-500">
                              {empData.phone}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {empData.department}
                            </div>
                            <div className="text-sm text-gray-500">
                              {empData.job_title}
                            </div>
                            {empData.hire_date && (
                              <div className="text-xs text-gray-400 mt-1">
                                Hired: {new Date(empData.hire_date).toLocaleDateString()}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col space-y-2">
                              <div className={`px-3 py-1.5 rounded text-xs font-medium flex items-center justify-between ${
                                systemPresence.servicetitan ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                <span>ServiceTitan</span>
                                {systemPresence.servicetitan && <Check className="h-3 w-3" />}
                              </div>
                              <div className={`px-3 py-1.5 rounded text-xs font-medium flex items-center justify-between ${
                                systemPresence.adp ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                <span>ADP</span>
                                {systemPresence.adp && <Check className="h-3 w-3" />}
                              </div>
                              <div className={`px-3 py-1.5 rounded text-xs font-medium flex items-center justify-between ${
                                systemPresence.azuga ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-600'
                              }`}>
                                <span>Azuga</span>
                                {systemPresence.azuga && <Check className="h-3 w-3" />}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredEmployees.length === 0 && (
                      <tr>
                        <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <Users className="h-12 w-12 text-gray-400 mb-2" />
                            {employeeSearch ? (
                              <>
                                <span>No employees found matching "{employeeSearch}"</span>
                                <span className="text-sm">Try a different search term</span>
                              </>
                            ) : (
                              <>
                                <span>No employee data available</span>
                                <span className="text-sm">Try refreshing the data</span>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination controls */}
            <Pagination />
          </div>
        </div>
      )}

      {/* Discrepancy Detail Modal */}
      {showDiscrepancyDetail && (
        <DiscrepancyDetailModal
          discrepancy={selectedDiscrepancy}
          onClose={closeDiscrepancyDetail}
        />
      )}
      {showDetailModal && (
        <DetailModal
          data={selectedDetailData}
          type={detailModalType}
          onClose={closeDetailModal}
        />
      )}
      {showTechnicianDetailModal && (
        <TechnicianDetailModal
          technicianData={technicianDetailData}
          onClose={closeTechnicianDetailModal}
        />
      )}
    </div>
  );
};

export default GoldLayer; 